import React, { useState, useEffect, useRef } from 'react';
import usePersistedTab from '../hooks/usePersistedTab';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Tabs,
  Tab,
  Grid,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Tooltip,
  Slider
} from '@mui/material';
import {
  Save,
  Refresh,
  RestartAlt,
  SmartToy,
  Message,
  People,
  TrendingUp,
  Settings,
  Psychology,
  Visibility,
  Campaign,
  Add,
  PlayArrow,
  Pause,
  Stop,
  Delete,
  Edit as EditIcon,
  FilterList,
  MenuBook,
  Search,
  Sync,
  Forum,
  Schedule,
  Security,
  EmojiEmotions,
  SportsEsports,
  Link as LinkIcon,
  Celebration,
  Star,
  PersonAdd,
  PersonRemove,
  Payments,
  Phone,
  SupervisorAccount,
  CardGiftcard,
  VolumeOff,
  VolumeUp
} from '@mui/icons-material';
import { adminAPI } from '../services/api';

const TabPanel = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

// Карточка статистики
const StatCard = ({ title, value, icon, color = '#4CAF50' }) => (
  <Card sx={{ bgcolor: 'rgba(255,255,255,0.05)', height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography color="textSecondary" variant="body2" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" sx={{ color }}>
            {value}
          </Typography>
        </Box>
        <Box sx={{ color, opacity: 0.7 }}>
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// Сколько звонков на страницу: больше — и список превращается
// в простыню, в которой ничего не найти.
const CALLS_PER_PAGE = 10;

// Время показываем московское — сервер и владелец живут по нему.
const msk = (value) => new Date(value).toLocaleString('ru-RU', {
  timeZone: 'Europe/Moscow', day: '2-digit', month: '2-digit', year: 'numeric',
  hour: '2-digit', minute: '2-digit', second: '2-digit',
}) + ' МСК';

// Отметка реплики от начала разговора — чтобы найти её в записи
const atMark = (sec) => `${Math.floor((sec || 0) / 60)}:`
  + String((sec || 0) % 60).padStart(2, '0');

// В расшифровке остаются пометки вроде [calm] и [chuckles] — они нужны
// голосу, чтобы играть интонацией, а читать их незачем.
const stripTags = (text) => String(text || '').replace(/\[[a-z -]{2,24}\]\s*/gi, '');

const Jarvis = () => {
  const [tabValue, setTabValue] = usePersistedTab('jarvis', 0);
  const [settings, setSettings] = useState({});
  const [stats, setStats] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [botStatus, setBotStatus] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Broadcasts state
  const [broadcasts, setBroadcasts] = useState([]);
  const [broadcastsLoading, setBroadcastsLoading] = useState(false);
  const [broadcastDialogOpen, setBroadcastDialogOpen] = useState(false);
  const [editingBroadcast, setEditingBroadcast] = useState(null);
  const [broadcastForm, setBroadcastForm] = useState({
    title: '',
    message_hint: '',
    call_to_action: '',
    delivery_style: 'soft',
    priority: 1,
    target_filter: {}
  });
  const [targetCount, setTargetCount] = useState(0);
  const [liveStatus, setLiveStatus] = useState(null);
  const [livePolling, setLivePolling] = useState(null);
  const [recipientsCount, setRecipientsCount] = useState(0);

  // Knowledge base state
  const [knowledge, setKnowledge] = useState([]);
  const [knowledgeCategories, setKnowledgeCategories] = useState([]);
  const [knowledgeLoading, setKnowledgeLoading] = useState(false);
  const [knowledgeSearch, setKnowledgeSearch] = useState('');
  const [knowledgeFilter, setKnowledgeFilter] = useState('all');
  const [knowledgeDialogOpen, setKnowledgeDialogOpen] = useState(false);
  const [editingKnowledge, setEditingKnowledge] = useState(null);
  const [knowledgeForm, setKnowledgeForm] = useState({ text: '', category: 'general' });
  const [knowledgeStats, setKnowledgeStats] = useState(null);

  // Group chat state
  const [groupChatSettings, setGroupChatSettings] = useState({});
  const [groupChatStats, setGroupChatStats] = useState(null);
  const [allowedDomains, setAllowedDomains] = useState([]);
  const [groupChatLoading, setGroupChatLoading] = useState(false);
  const [domainDialogOpen, setDomainDialogOpen] = useState(false);
  const [newDomain, setNewDomain] = useState({ domain: '', description: '' });

  // Forced Level 2 state
  const [forcedLevel2Users, setForcedLevel2Users] = useState([]);
  const [forcedLevel2Loading, setForcedLevel2Loading] = useState(false);
  const [forcedLevel2Dialog, setForcedLevel2Dialog] = useState(false);
  const [forcedLevel2Form, setForcedLevel2Form] = useState({ identifier: '', note: '' });
  // Индивидуальные лимиты генераций картинок (jarvis_image_limits)
  const [imageLimits, setImageLimits] = useState([]);
  const [imageLimitDialog, setImageLimitDialog] = useState(false);
  const [imageLimitForm, setImageLimitForm] = useState({ identifier: '', limit: '', note: '' });
  // Поимённый доступ к видео (jarvis_video_access) — подпиской НЕ открывается
  const [videoAccess, setVideoAccess] = useState([]);
  const [videoAccessDialog, setVideoAccessDialog] = useState(false);
  const [videoAccessForm, setVideoAccessForm] = useState({ identifier: '', note: '' });

  // 🎁 Менеджеры и тестовые доступы
  const [managers, setManagers] = useState([]);
  const [managersLoading, setManagersLoading] = useState(false);
  const [managerDialog, setManagerDialog] = useState(false);
  const [managerForm, setManagerForm] = useState({ identifier: '', note: '' });
  const [trialGrants, setTrialGrants] = useState([]);
  const [trialStats, setTrialStats] = useState(null);
  const [trialDays, setTrialDays] = useState(3);
  const [trialDaysInput, setTrialDaysInput] = useState('3');
  const [trialDialog, setTrialDialog] = useState(false);
  const [trialForm, setTrialForm] = useState({ identifier: '', days: '', note: '' });
  const [trialBusy, setTrialBusy] = useState(false);
  const [trialFilter, setTrialFilter] = useState('');   // telegram_id менеджера

  // 💎 Платёжка Jarvis Premium
  // Голосовые звонки: список звонивших и расшифровка выбранного
  const [callPeople, setCallPeople] = useState([]);
  const [callTotals, setCallTotals] = useState(null);
  const [callLoading, setCallLoading] = useState(false);
  const [callOpen, setCallOpen] = useState(null);   // telegram_id раскрытого
  const [callRows, setCallRows] = useState([]);
  const [callError, setCallError] = useState('');
  const [callAudio, setCallAudio] = useState({});
  const [openCall, setOpenCall] = useState(null);   // раскрытый звонок
  const [callPage, setCallPage] = useState(1);

  const [premiumStats, setPremiumStats] = useState(null);
  const [premiumLoading, setPremiumLoading] = useState(false);
  const [premiumView, setPremiumView] = useState('wallets'); // wallets|subscribers|payments|gifts|prices
  const [premiumRows, setPremiumRows] = useState([]);
  const [premiumPage, setPremiumPage] = useState(1);
  const [premiumTotal, setPremiumTotal] = useState(0);
  const [premiumPageSize, setPremiumPageSize] = useState(10);
  // 🏦 Казна: сборные кошельки, живые балансы, вывод, газ
  const [treasury, setTreasury] = useState(null);
  const treasuryReq = useRef(0); // номер последнего запроса казны
  const [treasuryLoading, setTreasuryLoading] = useState(false);
  const [treasuryBusy, setTreasuryBusy] = useState(''); // ключ текущей он-чейн операции
  const [sendForm, setSendForm] = useState({ network: 'bsc', asset: 'usdt', to: '', amount: '' });
  // «Собрать все»: прогресс фонового сбора (BEP-20 + TON) и таймер опроса
  const [sweepAll, setSweepAll] = useState(null);
  const sweepAllTimer = useRef(null);
  const [priceForm, setPriceForm] = useState({ identifier: '', price: '' });
  // Ответ по назначению цены — показываем рядом с формой (общий Alert
  // висит вверху страницы, снизу его не видно).
  const [priceMsg, setPriceMsg] = useState({ type: '', text: '' });

  // Загрузка данных
  const loadData = async () => {
    setLoading(true);
    try {
      const [settingsRes, statsRes, conversationsRes, statusRes] = await Promise.all([
        adminAPI.get('/jarvis/settings'),
        adminAPI.get('/jarvis/stats'),
        adminAPI.get('/jarvis/conversations'),
        adminAPI.get('/jarvis/status')
      ]);

      // Преобразуем массив настроек в объект
      const settingsObj = {};
      if (settingsRes.data.settings) {
        settingsRes.data.settings.forEach(s => {
          settingsObj[s.key] = s.value;
        });
      }
      setSettings(settingsObj);
      setStats(statsRes.data.stats);
      setConversations(conversationsRes.data.conversations || []);
      setBotStatus(statusRes.data);
    } catch (error) {
      console.error('Error loading Jarvis data:', error);
      setMessage({ type: 'error', text: 'Ошибка загрузки данных' });
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
    loadBroadcasts();
  }, []);

  // опрос «Собрать все» не должен жить после ухода со страницы
  useEffect(() => () => clearTimeout(sweepAllTimer.current), []);

  // Вкладку «Звонки» можно открыть не только кликом (свайп, стрелки
  // прокрутки), поэтому грузим по факту показа, а не по onClick.
  useEffect(() => {
    if (tabValue === 6) loadCalls();
    if (tabValue === 7) loadManagers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue]);

  // Сохранение настроек.
  // Пишем ТОЛЬКО реально редактируемые в UI ключи, чтобы "Сохранить" не
  // затирал остальные (зашитые) настройки Jarvis их экранными значениями.
  const SAVABLE_KEYS = ['video_model', 'system_prompt_level1', 'system_prompt_level2_addition',
    'music_enabled', 'music_model', 'music_daily_limit', 'music_lyrics_models',
    'music_video_enabled', 'music_wav_enabled', 'music_demo_limit',
    'demo_enabled', 'demo_days'];
  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const k of SAVABLE_KEYS) {
        if (settings[k] !== undefined) payload[k] = settings[k];
      }
      await adminAPI.post('/jarvis/settings', { settings: payload });
      setMessage({ type: 'success', text: 'Настройки сохранены' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Ошибка сохранения' });
    }
    setSaving(false);
  };

  // Перезапуск бота
  const handleRestart = async () => {
    try {
      await adminAPI.post('/jarvis/restart');
      setMessage({ type: 'success', text: 'Jarvis перезапущен' });
      setTimeout(() => loadData(), 2000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Ошибка перезапуска' });
    }
  };

  // Просмотр диалога
  const handleViewConversation = async (conversation) => {
    setSelectedConversation(conversation);
    try {
      const res = await adminAPI.get(`/jarvis/conversations/${conversation.id}/messages`);
      setConversationMessages(res.data.messages || []);
      setDialogOpen(true);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  // ============ FORCED LEVEL 2 FUNCTIONS ============

  const loadForcedLevel2 = async () => {
    setForcedLevel2Loading(true);
    try {
      const res = await adminAPI.get('/jarvis/forced-level2');
      setForcedLevel2Users(res.data.users || []);
    } catch (error) {
      console.error('Error loading forced level2:', error);
    }
    try {
      const res = await adminAPI.get('/jarvis/image-limits');
      setImageLimits(res.data.limits || []);
    } catch (error) {
      console.error('Error loading image limits:', error);
    }
    try {
      const res = await adminAPI.get('/jarvis/video-access');
      setVideoAccess(res.data.users || []);
    } catch (error) {
      console.error('Error loading video access:', error);
    }
    setForcedLevel2Loading(false);
  };

  // 💎 Платёжка: загрузка статистики + текущего раздела
  const loadCalls = async () => {
    setCallLoading(true);
    try {
      setCallError('');
      const res = await adminAPI.get('/jarvis/calls');
      setCallPeople(res.data.people || []);
      setCallTotals(res.data.totals || null);
    } catch (error) {
      console.error('Error loading calls:', error);
      setCallPeople([]);
      setCallError(error?.response?.data?.error || error.message || 'не отвечает');
    }
    setCallLoading(false);
  };

  // Тег <audio> не отправляет токен авторизации, поэтому запись тянем
  // запросом и подсовываем плееру уже готовый файл из памяти.
  const playCall = async (id) => {
    try {
      const res = await adminAPI.get(`/jarvis/calls/${id}/audio`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'audio/mpeg' }));
      setCallAudio((prev) => ({ ...prev, [id]: url }));
    } catch (error) {
      console.error('Error loading call audio:', error);
      setCallError('запись не открылась');
    }
  };

  const openCalls = async (telegramId) => {
    if (callOpen === telegramId) { setCallOpen(null); setCallRows([]); return; }
    setCallOpen(telegramId);
    setCallRows([]);
    setOpenCall(null);
    setCallPage(1);
    try {
      const res = await adminAPI.get(`/jarvis/calls/${telegramId}`);
      setCallRows(res.data.calls || []);
    } catch (error) {
      console.error('Error loading transcripts:', error);
    }
  };

  const loadPremiumRows = async (view, page) => {
    const map = {
      wallets: '/jarvis/premium/wallets',
      subscribers: '/jarvis/premium/subscribers',
      payments: '/jarvis/premium/payments',
      gifts: '/jarvis/premium/gifts',
      prices: '/jarvis/premium/prices',
    };
    try {
      const url = map[view] + (view === 'prices' ? '' : `?page=${page}`);
      const res = await adminAPI.get(url);
      setPremiumRows(res.data.items || []);
      setPremiumTotal(res.data.total || (res.data.items || []).length);
      setPremiumPage(res.data.page || page);
      if (res.data.pageSize) setPremiumPageSize(res.data.pageSize);
    } catch (error) {
      console.error('Error loading premium rows:', error);
      setPremiumRows([]);
      setPremiumTotal(0);
    }
  };

  const loadPremium = async () => {
    setPremiumLoading(true);
    loadTreasury(); // казна грузится параллельно (живые он-чейн балансы, ~пара секунд)
    if (!sweepAllTimer.current) pollSweepAll(); // сбор мог идти, пока вкладка была закрыта
    try {
      const res = await adminAPI.get('/jarvis/premium/stats');
      setPremiumStats(res.data.stats || null);
    } catch (error) {
      console.error('Error loading premium stats:', error);
    }
    await loadPremiumRows(premiumView, 1);
    setPremiumLoading(false);
  };

  const switchPremiumView = async (view) => {
    // Чистим строки СРАЗУ: иначе новая таблица рендерит старые строки
    // другой формы → undefined.toFixed() → синий экран.
    setPremiumRows([]);
    setPremiumTotal(0);
    setPremiumView(view);
    setPremiumPage(1);
    await loadPremiumRows(view, 1);
  };

  const changePremiumPage = async (delta) => {
    const pages = Math.max(1, Math.ceil(premiumTotal / premiumPageSize));
    const next = Math.min(pages, Math.max(1, premiumPage + delta));
    if (next !== premiumPage) await loadPremiumRows(premiumView, next);
  };

  const savePremiumPrice = async () => {
    const identifier = (priceForm.identifier || '').trim();
    const price = parseFloat(priceForm.price);
    if (!identifier || !(price > 0)) {
      setPriceMsg({ type: 'error', text: 'Укажите @username или ID и цену больше 0' });
      return;
    }
    try {
      const res = await adminAPI.post('/jarvis/premium/price', { identifier, price });
      const prev = res.data.prevPrice;
      setPriceMsg({ type: 'success', text: prev != null
        ? `${res.data.label}: цена изменена $${prev} → $${res.data.price}/мес`
        : `${res.data.label}: назначена цена $${res.data.price}/мес` });
      setPriceForm({ identifier: '', price: '' });
      // Сразу открываем список цен: иначе после нажатия на экране
      // визуально ничего не меняется.
      await switchPremiumView('prices');
    } catch (error) {
      setPriceMsg({ type: 'error', text: error.response?.data?.error || 'Ошибка назначения цены' });
    }
  };

  const resetPremiumPrice = async (telegramId) => {
    try {
      await adminAPI.delete(`/jarvis/premium/price/${telegramId}`);
      setPriceMsg({ type: 'success', text: 'Индивидуальная цена сброшена — вернулась базовая' });
      await loadPremiumRows('prices', 1);
    } catch (error) {
      setPriceMsg({ type: 'error', text: 'Ошибка сброса цены' });
    }
  };

  // ============ 🏦 КАЗНА ПЛАТЁЖКИ ============

  // Баланс не пришёл (нода/tonapi не ответили) — «н/д», а не 0.00:
  // ноль тут читается как «кошелёк пуст» и толкает на ненужные действия.
  const fmtBal = (v, digits) => (v == null ? 'н/д' : v.toFixed(digits));

  // Несобранное по живым балансам казны (в базе снимок монитора, он отстаёт).
  // count — адреса от 0.5 USDT: столько возьмёт «Собрать все».
  const liveUncollected = treasury ? treasury.users.reduce((acc, u) => ({
    bsc: acc.bsc + (u.bscUsdt || 0),
    ton: acc.ton + (u.tonUsdt || 0),
    count: acc.count + ((u.bscUsdt || 0) >= 0.5) + ((u.tonUsdt || 0) >= 0.5),
  }), { bsc: 0, ton: 0, count: 0 }) : null;

  const loadTreasury = async () => {
    const seq = ++treasuryReq.current;
    setTreasuryLoading(true);
    try {
      const res = await adminAPI.get('/jarvis/premium/treasury', { timeout: 60000 });
      // медленный ответ обогнали более свежим — не затираем новые цифры
      if (seq !== treasuryReq.current) return;
      if (res.data.success) setTreasury(res.data);
      else setMessage({ type: 'error', text: res.data.error || 'Ошибка загрузки казны' });
    } catch (error) {
      if (seq !== treasuryReq.current) return;
      setMessage({ type: 'error', text: error.response?.data?.error || 'Казна недоступна (Jarvis запущен?)' });
    } finally {
      if (seq === treasuryReq.current) setTreasuryLoading(false);
    }
  };

  // Опрос «Собрать все» раз в 3с, пока сбор идёт; в конце — итог и свежие балансы
  const pollSweepAll = async (watching = false) => {
    sweepAllTimer.current = null;
    try {
      const res = await adminAPI.get('/jarvis/premium/treasury/sweep_all');
      setSweepAll(res.data);
      if (res.data.running) {
        setTreasuryBusy('sweep-all');
        sweepAllTimer.current = setTimeout(() => pollSweepAll(true), 3000);
        return;
      }
      if (!watching) return;
      setTreasuryBusy('');
      const c = res.data.collected || {};
      const errs = res.data.errors || [];
      setMessage({
        type: errs.length ? 'warning' : 'success',
        text: `Собрано: BEP-20 ${(c.bsc || 0).toFixed(2)} · TON ${(c.ton || 0).toFixed(2)} USDT`
          + (errs.length ? ` · не вышло: ${errs.join('; ')}` : ''),
      });
      await loadTreasury();
    } catch (error) {
      if (watching) {
        setTreasuryBusy('');
        setMessage({ type: 'error', text: 'Прогресс сбора недоступен — обнови балансы через минуту' });
      }
    }
  };

  const startSweepAll = async () => {
    const u = liveUncollected;
    if (!window.confirm(`Собрать USDT со всех адресов юзеров на сборные кошельки?\n`
      + `BEP-20: ${u.bsc.toFixed(2)} · TON: ${u.ton.toFixed(2)} USDT, адресов: ${u.count}.\n`
      + 'TON идёт около 40 с на адрес, можно не ждать на странице.')) return;
    setTreasuryBusy('sweep-all');
    try {
      const res = await adminAPI.post('/jarvis/premium/treasury/sweep_all', {}, { timeout: 60000 });
      setSweepAll(res.data);
      if (res.data.status === 'nothing') {
        setTreasuryBusy('');
        setMessage({ type: 'info', text: 'Собирать нечего: на адресах юзеров меньше 0.5 USDT' });
        return;
      }
      sweepAllTimer.current = setTimeout(() => pollSweepAll(true), 3000);
    } catch (error) {
      setTreasuryBusy('');
      setMessage({ type: 'error', text: error.response?.data?.error || 'Сбор не запустился' });
    }
  };

  const sweepWallet = async (telegramId, network) => {
    const net = network === 'bsc' ? 'BEP-20' : 'TON';
    if (!window.confirm(`Собрать весь USDT (${net}) с адреса юзера ${telegramId} на СБОРНЫЙ кошелёк?`)) return;
    setTreasuryBusy(`sweep-${telegramId}-${network}`);
    try {
      const res = await adminAPI.post('/jarvis/premium/treasury/sweep',
        { telegramId, network }, { timeout: 240000 });
      const d = res.data;
      if (d.success) {
        setMessage({ type: 'success', text: `Собрано ${Number(d.usdt).toFixed(2)} USDT (${net})${d.tx ? ` · tx ${d.tx.slice(0, 16)}…` : ''}` });
      } else {
        setMessage({ type: 'error', text: d.error || `Сбор не прошёл (${d.status})` });
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Ошибка сбора' });
    }
    setTreasuryBusy('');
    await loadTreasury();
  };

  // Отправка USDT с адреса юзера на ЛЮБОЙ адрес (кнопка «Отправить» в строке)
  const sendFromUser = async (r) => {
    const to = window.prompt(`Куда отправить USDT (${r.net}) с адреса юзера ${r.telegramId}?`);
    if (to === null) return;
    if (!to.trim()) { setMessage({ type: 'error', text: 'Нужен адрес получателя' }); return; }
    const amount = window.prompt(`Сколько USDT? (пусто = всё, доступно ${r.usdt.toFixed(2)})`, '');
    if (amount === null) return;
    if (amount && !(parseFloat(amount) > 0)) { setMessage({ type: 'error', text: 'Сумма должна быть больше 0' }); return; }
    if (!window.confirm(`Отправить ${amount || 'ВСЕ'} USDT (${r.net}) с адреса юзера ${r.telegramId} на ${to.trim()}?`)) return;
    setTreasuryBusy(`usend-${r.telegramId}-${r.network}`);
    try {
      const res = await adminAPI.post('/jarvis/premium/treasury/sweep',
        { telegramId: r.telegramId, network: r.network, to: to.trim(), amount: amount || undefined },
        { timeout: 240000 });
      const d = res.data;
      if (d.success) {
        setMessage({ type: 'success', text: `Отправлено ${Number(d.usdt).toFixed(2)} USDT (${r.net})${d.tx ? ` · tx ${d.tx.slice(0, 16)}…` : ''}` });
      } else {
        setMessage({ type: 'error', text: d.error || `Не отправлено (${d.status})` });
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Ошибка отправки' });
    }
    setTreasuryBusy('');
    await loadTreasury();
  };

  const sendFromMain = async () => {
    const { network, asset, to, amount } = sendForm;
    const net = network === 'bsc' ? 'BEP-20' : 'TON';
    const coin = asset === 'native' ? (network === 'bsc' ? 'BNB' : 'TON') : 'USDT';
    if (!to.trim()) { setMessage({ type: 'error', text: 'Укажи адрес получателя' }); return; }
    if (asset === 'native' && !(parseFloat(amount) > 0)) {
      setMessage({ type: 'error', text: `Для ${coin} нужна сумма` }); return;
    }
    const amountLabel = amount ? `${amount} ${coin}` : `весь ${coin}`;
    if (!window.confirm(`Отправить ${amountLabel} со СБОРНОГО (${net}) на ${to.trim()}?`)) return;
    setTreasuryBusy('send');
    try {
      const res = await adminAPI.post('/jarvis/premium/treasury/send',
        { network, asset, to: to.trim(), amount: amount || undefined }, { timeout: 180000 });
      const d = res.data;
      if (d.success) {
        setMessage({ type: 'success', text: `Отправлено ${Number(d.usdt ?? d.amount).toFixed(4)} ${coin}${d.tx ? ` · tx ${d.tx.slice(0, 16)}…` : ''}` });
        setSendForm({ ...sendForm, to: '', amount: '' });
      } else {
        setMessage({ type: 'error', text: d.error || `Не отправлено (${d.status})` });
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Ошибка отправки' });
    }
    setTreasuryBusy('');
    await loadTreasury();
  };

  const topupGas = async (telegramId, network) => {
    const coin = network === 'bsc' ? 'BNB' : 'TON';
    const def = network === 'bsc' ? treasury?.gasDefaults?.bnbTopup : treasury?.gasDefaults?.tonTopup;
    const amount = window.prompt(`Сколько ${coin} долить на адрес юзера ${telegramId} с главного кошелька?`, String(def ?? ''));
    if (amount === null) return;
    if (!(parseFloat(amount) > 0)) { setMessage({ type: 'error', text: 'Сумма должна быть больше 0' }); return; }
    setTreasuryBusy(`gas-${telegramId}-${network}`);
    try {
      const res = await adminAPI.post('/jarvis/premium/treasury/gas',
        { telegramId, network, amount: parseFloat(amount) }, { timeout: 90000 });
      const d = res.data;
      if (d.success) setMessage({ type: 'success', text: `⛽ ${amount} ${coin} отправлено юзеру ${telegramId}` });
      else setMessage({ type: 'error', text: d.error || `Газ не отправлен (${d.status})` });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Ошибка отправки газа' });
    }
    setTreasuryBusy('');
    await loadTreasury();
  };

  const grantForcedLevel2 = async () => {
    try {
      const { identifier, note } = forcedLevel2Form;
      if (!identifier.trim()) return;

      const res = await adminAPI.post('/jarvis/forced-level2', { identifier: identifier.trim(), note });
      const info = res.data.found_username
        ? `@${res.data.found_username} (${res.data.found_telegram_id})`
        : String(res.data.found_telegram_id);
      const extra = res.data.user_exists === false ? ' (ещё не в базе — Level 2 активируется при первом входе)' : '';
      setMessage({ type: 'success', text: `Level 2 выдан: ${info}${extra}` });
      setTimeout(() => setMessage({ type: '', text: '' }), 7000);
      setForcedLevel2Dialog(false);
      setForcedLevel2Form({ identifier: '', note: '' });
      loadForcedLevel2();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Ошибка выдачи Level 2' });
    }
  };

  const revokeForcedLevel2 = async (record) => {
    const label = record.username
      ? '@' + record.username
      : (record.telegram_id || `id=${record.id}`);
    if (!window.confirm(`Отозвать Level 2 у ${label}?`)) return;
    try {
      await adminAPI.delete(`/jarvis/forced-level2/${record.id}`);
      setMessage({ type: 'success', text: `Level 2 отозван у ${label}` });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      loadForcedLevel2();
    } catch (error) {
      setMessage({ type: 'error', text: 'Ошибка отзыва Level 2' });
    }
  };

  // ============ МЕНЕДЖЕРЫ И ТЕСТОВЫЕ ДОСТУПЫ ============

  const loadManagers = async (managerFilter = trialFilter) => {
    setManagersLoading(true);
    try {
      const [mRes, tRes] = await Promise.all([
        adminAPI.get('/jarvis/managers'),
        adminAPI.get('/jarvis/trials', {
          params: managerFilter ? { manager: managerFilter } : {},
        }),
      ]);
      setManagers(mRes.data.managers || []);
      setTrialDays(mRes.data.trial_days || 3);
      setTrialDaysInput(String(mRes.data.trial_days || 3));
      setTrialGrants(tRes.data.grants || []);
      setTrialStats(tRes.data.stats || null);
    } catch (error) {
      setMessage({ type: 'error', text: 'Ошибка загрузки менеджеров' });
    }
    setManagersLoading(false);
  };

  const addManager = async () => {
    const { identifier, note } = managerForm;
    if (!identifier.trim()) return;
    try {
      const res = await adminAPI.post('/jarvis/managers', { identifier: identifier.trim(), note });
      const m = res.data.manager;
      const extra = res.data.user_exists === false
        ? ' (в базе игры его нет — права включатся, как только он напишет Джарвису)'
        : '';
      setMessage({
        type: 'success',
        text: `Менеджер назначен: ${m.username ? '@' + m.username : m.telegram_id}${extra}`,
      });
      setTimeout(() => setMessage({ type: '', text: '' }), 7000);
      setManagerDialog(false);
      setManagerForm({ identifier: '', note: '' });
      loadManagers();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Ошибка назначения менеджера' });
    }
  };

  const removeManager = async (record) => {
    const label = record.username ? '@' + record.username : String(record.telegram_id);
    if (!window.confirm(`Снять менеджера ${label}? Ранее выданные им доступы останутся активными.`)) return;
    try {
      await adminAPI.delete(`/jarvis/managers/${record.id}`);
      setMessage({ type: 'success', text: `Менеджер снят: ${label}` });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      loadManagers();
    } catch (error) {
      setMessage({ type: 'error', text: 'Ошибка снятия менеджера' });
    }
  };

  const grantTrial = async () => {
    const { identifier, days, note } = trialForm;
    if (!identifier.trim()) return;
    setTrialBusy(true);
    try {
      const res = await adminAPI.post('/jarvis/trials', {
        identifier: identifier.trim(),
        days: days ? Number(days) : undefined,
        note,
      }, { timeout: 60000 });
      const d = res.data;
      const until = new Date(d.expires_at).toLocaleString('ru-RU', {
        timeZone: 'Europe/Moscow', day: '2-digit', month: '2-digit',
        hour: '2-digit', minute: '2-digit',
      });
      const tail = d.notified
        ? 'Человеку написал.'
        : '⚠️ Написать не смог — он ни разу не писал Джарвису. Доступ активен.';
      setMessage({ type: 'success', text: `Доступ выдан ${d.label} до ${until} МСК. ${tail}` });
      setTimeout(() => setMessage({ type: '', text: '' }), 9000);
      setTrialDialog(false);
      setTrialForm({ identifier: '', days: '', note: '' });
      loadManagers();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Ошибка выдачи доступа' });
    }
    setTrialBusy(false);
  };

  const revokeTrial = async (row) => {
    const label = row.current_username || row.username
      ? '@' + (row.current_username || row.username)
      : String(row.telegram_id);
    if (!window.confirm(`Отозвать доступ у ${label}? Он сразу вернётся на Level 1.`)) return;
    try {
      await adminAPI.delete(`/jarvis/trials/${row.id}`);
      setMessage({ type: 'success', text: `Доступ отозван у ${label}` });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      loadManagers();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Ошибка отзыва доступа' });
    }
  };

  const saveTrialDays = async () => {
    const days = Number(trialDaysInput);
    if (!(days >= 1 && days <= 90)) {
      setMessage({ type: 'error', text: 'Срок — от 1 до 90 дней' });
      return;
    }
    try {
      await adminAPI.put('/jarvis/trial-days', { days });
      setTrialDays(days);
      setMessage({ type: 'success', text: `Срок по умолчанию: ${days} дн.` });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Ошибка сохранения срока' });
    }
  };

  // ============ ЛИМИТЫ КАРТИНОК ============

  const saveImageLimit = async () => {
    try {
      const { identifier, limit, note } = imageLimitForm;
      if (!identifier.trim() || limit === '') return;

      const res = await adminAPI.post('/jarvis/image-limits', {
        identifier: identifier.trim(),
        limit: Number(limit),
        note,
      });
      const info = res.data.found_username
        ? `@${res.data.found_username} (${res.data.found_telegram_id})`
        : String(res.data.found_telegram_id);
      setMessage({ type: 'success', text: `Лимит картинок ${limit}/день задан: ${info}` });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      setImageLimitDialog(false);
      setImageLimitForm({ identifier: '', limit: '', note: '' });
      loadForcedLevel2();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Ошибка установки лимита' });
    }
  };

  const removeImageLimit = async (record) => {
    const label = record.current_username || record.username
      ? '@' + (record.current_username || record.username)
      : String(record.telegram_id);
    if (!window.confirm(`Убрать индивидуальный лимит у ${label}? Вернётся дефолтный.`)) return;
    try {
      await adminAPI.delete(`/jarvis/image-limits/${record.telegram_id}`);
      setMessage({ type: 'success', text: `Лимит убран у ${label}` });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      loadForcedLevel2();
    } catch (error) {
      setMessage({ type: 'error', text: 'Ошибка удаления лимита' });
    }
  };

  // ============ ДОСТУП К ВИДЕО ============

  const saveVideoAccess = async () => {
    try {
      const { identifier, note } = videoAccessForm;
      if (!identifier.trim()) return;

      const res = await adminAPI.post('/jarvis/video-access', {
        identifier: identifier.trim(),
        note,
      });
      const info = res.data.found_username
        ? `@${res.data.found_username}${res.data.found_telegram_id ? ` (${res.data.found_telegram_id})` : ''}`
        : String(res.data.found_telegram_id);
      setMessage({
        type: 'success',
        text: `Видео открыто: ${info}${res.data.user_exists ? '' : ' — юзера ещё нет в базе игры, привяжется при первом обращении'}`,
      });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      setVideoAccessDialog(false);
      setVideoAccessForm({ identifier: '', note: '' });
      loadForcedLevel2();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Ошибка выдачи доступа' });
    }
  };

  const removeVideoAccess = async (record) => {
    const label = record.current_username || record.username
      ? '@' + (record.current_username || record.username)
      : String(record.telegram_id);
    if (!window.confirm(`Закрыть доступ к видео для ${label}?`)) return;
    try {
      await adminAPI.delete(`/jarvis/video-access/${record.id}`);
      setMessage({ type: 'success', text: `Доступ к видео закрыт: ${label}` });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      loadForcedLevel2();
    } catch (error) {
      setMessage({ type: 'error', text: 'Ошибка закрытия доступа' });
    }
  };

  // ============ BROADCASTS FUNCTIONS ============

  // Загрузка рассылок
  const loadBroadcasts = async () => {
    setBroadcastsLoading(true);
    try {
      const res = await adminAPI.get('/jarvis/broadcasts');
      setBroadcasts(res.data.broadcasts || []);
      // Загружаем кол-во получателей
      try {
        const rc = await adminAPI.get('/jarvis/broadcasts/recipients-count');
        setRecipientsCount(rc.data.count || 0);
      } catch (e) { /* нефатально */ }
    } catch (error) {
      console.error('Error loading broadcasts:', error);
    }
    setBroadcastsLoading(false);
  };

  // Live-статус рассылки (поллинг каждые 5 сек)
  const startLivePolling = (broadcastId) => {
    if (livePolling) clearInterval(livePolling);
    const fetchStatus = async () => {
      try {
        const res = await adminAPI.get(`/jarvis/broadcasts/${broadcastId}/live-status`);
        setLiveStatus(res.data);
        // Если завершено — остановить поллинг
        if (res.data.status === 'completed' || res.data.status === 'cancelled') {
          stopLivePolling();
          loadBroadcasts();
        }
      } catch (e) { console.error('Live status error:', e); }
    };
    fetchStatus();
    const interval = setInterval(fetchStatus, 5000);
    setLivePolling(interval);
  };

  const stopLivePolling = () => {
    if (livePolling) {
      clearInterval(livePolling);
      setLivePolling(null);
    }
    setLiveStatus(null);
  };

  // Открыть диалог создания/редактирования
  const openBroadcastDialog = (broadcast = null) => {
    if (broadcast) {
      setEditingBroadcast(broadcast);
      setBroadcastForm({
        title: broadcast.title || '',
        message_hint: broadcast.message_hint || '',
        call_to_action: broadcast.call_to_action || '',
        delivery_style: broadcast.delivery_style || 'soft',
        priority: broadcast.priority || 1,
        target_filter: broadcast.target_filter || {}
      });
    } else {
      setEditingBroadcast(null);
      setBroadcastForm({
        title: '',
        message_hint: '',
        call_to_action: '',
        delivery_style: 'soft',
        priority: 1,
        target_filter: {}
      });
    }
    setBroadcastDialogOpen(true);
  };

  // Сохранить рассылку
  const saveBroadcast = async () => {
    try {
      if (editingBroadcast) {
        await adminAPI.put(`/jarvis/broadcasts/${editingBroadcast.id}`, broadcastForm);
        setMessage({ type: 'success', text: 'Рассылка обновлена' });
      } else {
        await adminAPI.post('/jarvis/broadcasts', broadcastForm);
        setMessage({ type: 'success', text: 'Рассылка создана' });
      }
      setBroadcastDialogOpen(false);
      loadBroadcasts();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Ошибка сохранения рассылки' });
    }
  };

  // Запустить рассылку
  const startBroadcast = async (id) => {
    try {
      const res = await adminAPI.post(`/jarvis/broadcasts/${id}/start`);
      const sending = res.data.sending || {};
      setMessage({
        type: 'success',
        text: `Рассылка запущена: ${sending.total_recipients || '?'} получателей, ${sending.variations_count || '?'} вариаций (~${sending.estimated_minutes || '?'} мин)`
      });
      loadBroadcasts();
      startLivePolling(id);
      setTimeout(() => setMessage({ type: '', text: '' }), 8000);
    } catch (error) {
      const errMsg = error.response?.data?.error || 'Ошибка запуска рассылки';
      setMessage({ type: 'error', text: errMsg });
    }
  };

  // Приостановить рассылку
  const pauseBroadcast = async (id) => {
    try {
      await adminAPI.post(`/jarvis/broadcasts/${id}/pause`);
      setMessage({ type: 'success', text: 'Рассылка приостановлена' });
      loadBroadcasts();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Ошибка приостановки рассылки' });
    }
  };

  // Возобновить рассылку (из паузы — resume, не новый start)
  const resumeBroadcast = async (id) => {
    try {
      await adminAPI.post(`/jarvis/broadcasts/${id}/resume`);
      setMessage({ type: 'success', text: 'Рассылка возобновлена (пропускает уже доставленных)' });
      loadBroadcasts();
      startLivePolling(id);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } catch (error) {
      const errMsg = error.response?.data?.error || 'Ошибка возобновления рассылки';
      setMessage({ type: 'error', text: errMsg });
    }
  };

  // Завершить рассылку
  const completeBroadcast = async (id) => {
    try {
      await adminAPI.post(`/jarvis/broadcasts/${id}/complete`);
      setMessage({ type: 'success', text: 'Рассылка завершена' });
      stopLivePolling();
      loadBroadcasts();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Ошибка завершения рассылки' });
    }
  };

  // Удалить рассылку
  const deleteBroadcast = async (id) => {
    if (!window.confirm('Удалить рассылку?')) return;
    try {
      await adminAPI.delete(`/jarvis/broadcasts/${id}`);
      setMessage({ type: 'success', text: 'Рассылка удалена' });
      loadBroadcasts();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Ошибка удаления рассылки' });
    }
  };

  // Получить цвет статуса рассылки
  const getBroadcastStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'default';
      case 'active': return 'success';
      case 'paused': return 'warning';
      case 'completed': return 'info';
      default: return 'default';
    }
  };

  // Получить название статуса
  const getBroadcastStatusLabel = (status) => {
    switch (status) {
      case 'draft': return 'Черновик';
      case 'active': return 'Активна';
      case 'paused': return 'Приостановлена';
      case 'completed': return 'Завершена';
      default: return status;
    }
  };

  // Обновление настройки
  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  // Тумблеры, которые применяются СРАЗУ, без общей кнопки «Сохранить».
  //
  // ⚠️ Раньше их onChange звал updateSetting — а он меняет только состояние
  // в браузере. Ключей `use_direct_vendors` и `image_direct_first` нет в
  // SAVABLE_KEYS, поэтому «Сохранить» их тоже не отправлял: тумблер щёлкал,
  // а в базе ничего не менялось. Починено 09.09.2026.
  const [settingSaving, setSettingSaving] = useState('');
  const saveSettingNow = async (key, value) => {
    const before = settings[key];
    setSettings(prev => ({ ...prev, [key]: value }));
    setSettingSaving(key);
    try {
      await adminAPI.put(`/jarvis/settings/${key}`, { value });
      setMessage({ type: 'success', text: 'Применено' });
      setTimeout(() => setMessage({ type: '', text: '' }), 2000);
    } catch (error) {
      setSettings(prev => ({ ...prev, [key]: before }));
      setMessage({ type: 'error', text: 'Не удалось сохранить' });
    }
    setSettingSaving('');
  };

  // ============ KNOWLEDGE BASE FUNCTIONS ============

  // Загрузка базы знаний
  const loadKnowledge = async () => {
    setKnowledgeLoading(true);
    try {
      const params = new URLSearchParams();
      if (knowledgeFilter && knowledgeFilter !== 'all') params.append('category', knowledgeFilter);
      if (knowledgeSearch) params.append('search', knowledgeSearch);

      const [knowledgeRes, statsRes] = await Promise.all([
        adminAPI.get(`/jarvis/knowledge?${params.toString()}`),
        adminAPI.get('/jarvis/knowledge/stats')
      ]);

      setKnowledge(knowledgeRes.data.knowledge || []);
      setKnowledgeCategories(knowledgeRes.data.categories || []);
      setKnowledgeStats(statsRes.data.stats || null);
    } catch (error) {
      console.error('Error loading knowledge:', error);
      setMessage({ type: 'error', text: 'Ошибка загрузки базы знаний' });
    }
    setKnowledgeLoading(false);
  };

  // Открыть диалог добавления/редактирования
  const openKnowledgeDialog = (item = null) => {
    if (item) {
      setEditingKnowledge(item);
      setKnowledgeForm({ text: item.text, category: item.category });
    } else {
      setEditingKnowledge(null);
      setKnowledgeForm({ text: '', category: 'general' });
    }
    setKnowledgeDialogOpen(true);
  };

  // Сохранить знание
  const saveKnowledge = async () => {
    try {
      if (editingKnowledge) {
        await adminAPI.put(`/jarvis/knowledge/${editingKnowledge.id}`, knowledgeForm);
        setMessage({ type: 'success', text: 'Знание обновлено' });
      } else {
        await adminAPI.post('/jarvis/knowledge', knowledgeForm);
        setMessage({ type: 'success', text: 'Знание добавлено' });
      }
      setKnowledgeDialogOpen(false);
      loadKnowledge();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Ошибка сохранения' });
    }
  };

  // Удалить знание
  const deleteKnowledge = async (id) => {
    if (!window.confirm('Удалить эту запись из базы знаний?')) return;
    try {
      await adminAPI.delete(`/jarvis/knowledge/${id}`);
      setMessage({ type: 'success', text: 'Запись удалена' });
      loadKnowledge();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Ошибка удаления' });
    }
  };

  // Синхронизация с RAG
  const syncKnowledge = async () => {
    try {
      setMessage({ type: 'info', text: 'Синхронизация...' });
      await adminAPI.post('/jarvis/knowledge/sync');
      setMessage({ type: 'success', text: 'База знаний синхронизирована с RAG' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Ошибка синхронизации' });
    }
  };

  // ============ GROUP CHAT FUNCTIONS ============

  // Загрузка настроек группового чата
  const loadGroupChatSettings = async () => {
    setGroupChatLoading(true);
    try {
      const [settingsRes, domainsRes] = await Promise.all([
        adminAPI.get('/jarvis/group-chat/settings'),
        adminAPI.get('/jarvis/group-chat/domains')
      ]);
      setGroupChatSettings(settingsRes.data.settings || {});
      setGroupChatStats(settingsRes.data.stats || null);
      setAllowedDomains(domainsRes.data.domains || []);
    } catch (error) {
      console.error('Error loading group chat settings:', error);
      setMessage({ type: 'error', text: 'Ошибка загрузки настроек группового чата' });
    }
    setGroupChatLoading(false);
  };

  // Сохранить настройки группового чата
  const saveGroupChatSettings = async () => {
    try {
      await adminAPI.post('/jarvis/group-chat/settings', { settings: groupChatSettings });
      setMessage({ type: 'success', text: 'Настройки группового чата сохранены' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Ошибка сохранения настроек' });
    }
  };

  // Обновить настройку группового чата
  const updateGroupChatSetting = (key, value) => {
    setGroupChatSettings(prev => ({ ...prev, [key]: value }));
  };

  // Рубильник публикаций Джарвиса в группах.
  // Сохраняем СРАЗУ, не дожидаясь кнопки «Сохранить»: это аварийный
  // выключатель, он должен срабатывать одним кликом.
  const toggleGroupPublications = async (enabled) => {
    const value = enabled ? 'true' : 'false';
    const prev = groupChatSettings.group_publications_enabled;
    setGroupChatSettings(p => ({ ...p, group_publications_enabled: value }));
    try {
      await adminAPI.post('/jarvis/group-chat/settings', {
        settings: { group_publications_enabled: value }
      });
      setMessage({
        type: 'success',
        text: enabled
          ? 'Джарвис снова пишет в группы'
          : 'Джарвис замолчал в группах (применится в течение 5 секунд)'
      });
      setTimeout(() => setMessage({ type: '', text: '' }), 4000);
    } catch (error) {
      setGroupChatSettings(p => ({ ...p, group_publications_enabled: prev }));
      setMessage({ type: 'error', text: 'Не удалось переключить рубильник' });
    }
  };

  // Добавить разрешённый домен
  const addAllowedDomain = async () => {
    if (!newDomain.domain.trim()) return;
    try {
      await adminAPI.post('/jarvis/group-chat/domains', newDomain);
      setMessage({ type: 'success', text: 'Домен добавлен' });
      setDomainDialogOpen(false);
      setNewDomain({ domain: '', description: '' });
      loadGroupChatSettings();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Ошибка добавления домена' });
    }
  };

  // Удалить разрешённый домен
  const deleteAllowedDomain = async (id) => {
    if (!window.confirm('Удалить этот домен?')) return;
    try {
      await adminAPI.delete(`/jarvis/group-chat/domains/${id}`);
      setMessage({ type: 'success', text: 'Домен удалён' });
      loadGroupChatSettings();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Ошибка удаления домена' });
    }
  };

  // Получить цвет категории
  const getCategoryColor = (category) => {
    const colors = {
      'staking': '#4CAF50',
      'referral': '#2196F3',
      'faq': '#FF9800',
      'token': '#9C27B0',
      'ranks': '#E91E63',
      'vip': '#FFD700',
      'minigames': '#00BCD4',
      'mechanics': '#607D8B',
      'overview': '#3F51B5',
      'social': '#795548',
      'general': '#9E9E9E'
    };
    return colors[category] || '#9E9E9E';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* flexWrap: на мобиле кнопки переносятся под заголовок,
          иначе «Сохранить» уезжает за правый край экрана */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SmartToy /> Jarvis AI Assistant
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label={botStatus?.status === 'online' ? 'Online' : 'Offline'}
            color={botStatus?.status === 'online' ? 'success' : 'error'}
            size="small"
          />
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadData}
          >
            Обновить
          </Button>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<RestartAlt />}
            onClick={handleRestart}
          >
            Перезапустить
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </Box>
      </Box>

      {message.text && (
        <Alert severity={message.type} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}

      <Paper sx={{ bgcolor: 'rgba(255,255,255,0.02)' }}>
        <Tabs
          value={tabValue}
          onChange={(e, v) => setTabValue(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ borderBottom: 1, borderColor: 'divider', px: 0, minHeight: 48,
            '& .MuiTab-root': { minWidth: 'auto', px: 1.5, py: 1, fontSize: '0.75rem', minHeight: 48 },
            '& .MuiTabScrollButton-root': { width: 28 }
          }}
        >
          <Tab icon={<Campaign />} label="Рассылки" onClick={loadBroadcasts} />
          <Tab icon={<TrendingUp />} label="Статистика" />
          <Tab icon={<Settings />} label="Настройки" />
          <Tab icon={<MenuBook />} label="База знаний" onClick={loadKnowledge} />
          <Tab icon={<Star />} label="Level 2" onClick={loadForcedLevel2} />
          <Tab icon={<Payments />} label="Платёжка" onClick={loadPremium} />
          <Tab icon={<Phone />} label="Звонки" onClick={loadCalls} />
          <Tab icon={<SupervisorAccount />} label="Менеджеры" onClick={() => loadManagers()} />
        </Tabs>

        {/* Статистика */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Сообщений сегодня"
                  value={stats?.today_messages || 0}
                  icon={<Message sx={{ fontSize: 40 }} />}
                  color="#2196F3"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Новых диалогов сегодня"
                  value={stats?.today_conversations || 0}
                  icon={<People sx={{ fontSize: 40 }} />}
                  color="#4CAF50"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Всего сообщений"
                  value={stats?.total_messages || 0}
                  icon={<Message sx={{ fontSize: 40 }} />}
                  color="#FF9800"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Всего диалогов"
                  value={stats?.total_conversations || 0}
                  icon={<People sx={{ fontSize: 40 }} />}
                  color="#9C27B0"
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Топ пользователей
            </Typography>
            <TableContainer component={Paper} sx={{ bgcolor: 'rgba(255,255,255,0.02)' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Пользователь</TableCell>
                    <TableCell>Telegram ID</TableCell>
                    <TableCell align="right">Сообщений</TableCell>
                    <TableCell align="right">Голосовых</TableCell>
                    <TableCell>Последняя активность</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats?.top_users?.map((user, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        {user.first_name || user.username || 'Без имени'}
                      </TableCell>
                      <TableCell>{user.telegram_id}</TableCell>
                      <TableCell align="right">{user.messages_count}</TableCell>
                      <TableCell align="right">{user.voice_messages}</TableCell>
                      <TableCell>
                        {user.last_message_at
                          ? new Date(user.last_message_at).toLocaleString('ru-RU')
                          : '-'}
                      </TableCell>
                    </TableRow>
                  )) || (
                    <TableRow>
                      <TableCell colSpan={5} align="center">Нет данных</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider sx={{ my: 3 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.02)' }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Среднее время ответа
                  </Typography>
                  <Typography variant="h5">
                    {stats?.avg_response_time_ms || 0} мс
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.02)' }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Среднее токенов на ответ
                  </Typography>
                  <Typography variant="h5">
                    {stats?.avg_tokens_per_response || 0}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.02)' }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Всего использовано токенов
                  </Typography>
                  <Typography variant="h5">
                    {stats?.total_tokens_used?.toLocaleString() || 0}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        {/* Настройки */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ p: { xs: 1.5, sm: 3 } }}>

            {/* Переход на прямые API вендоров */}
            <Paper
              sx={{
                p: { xs: 2, sm: 2.5 },
                mb: 3,
                border: '1px solid',
                borderColor: settings.use_direct_vendors === 'true' ? 'success.main' : 'divider',
                bgcolor: settings.use_direct_vendors === 'true' ? 'success.dark' : 'background.paper',
                opacity: settings.use_direct_vendors === 'true' ? 0.95 : 1
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: 240 }}>
                  <Typography variant="h6" sx={{ mb: 0.5 }}>
                    Брать модели напрямую у вендоров
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    Выключено — всё идёт через api.navy, как раньше.
                    Включено — OpenAI и Google отвечают напрямую: дешевле
                    за счёт кэша промпта и Batch, и мы больше не зависим
                    от того, пополнил ли посредник свой баланс.
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Модели, у которых нет прямого ключа (Claude, Grok, DeepSeek),
                    продолжат идти через api.navy — ничего не сломается.
                  </Typography>
                </Box>
                <Switch
                  checked={settings.use_direct_vendors === 'true'}
                  disabled={settingSaving === 'use_direct_vendors'}
                  onChange={(e) => saveSettingNow('use_direct_vendors', e.target.checked ? 'true' : 'false')}
                  color="success"
                />
              </Box>
            </Paper>

            {/* Откуда берём картинки. Отдельно от моделей: у картинок своя беда. */}
            <Paper
              sx={{
                p: { xs: 2, sm: 2.5 },
                mb: 3,
                border: '1px solid',
                borderColor: settings.image_direct_first === 'true' ? 'warning.main' : 'divider',
                bgcolor: settings.image_direct_first === 'true' ? 'warning.dark' : 'background.paper',
                opacity: settings.image_direct_first === 'true' ? 0.95 : 1
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: 240 }}>
                  <Typography variant="h6" sx={{ mb: 0.5 }}>
                    Картинки с эталоном — сразу напрямую
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    Касается ТОЛЬКО кадров, где есть эталон: фото человека,
                    логотип, образец стиля. Включено — такой кадр уходит
                    прямым каналом пачкой, вдвое дешевле обычного запроса.
                    Выключено — как обычно, сначала прежний путь.
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Картинки БЕЗ эталона тумблер не трогает — они всегда
                    начинают с прежнего пути, там их рисуют честно.
                    Включать, когда прежний путь теряет эталон и подставляет
                    чужое лицо или чужой логотип.
                  </Typography>
                </Box>
                <Switch
                  checked={settings.image_direct_first === 'true'}
                  disabled={settingSaving === 'image_direct_first'}
                  onChange={(e) => saveSettingNow('image_direct_first', e.target.checked ? 'true' : 'false')}
                  color="warning"
                />
              </Box>
            </Paper>

            <Typography variant="h6" gutterBottom>
              Оживление картинки (видео)
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Модель оживления видео</InputLabel>
                  <Select
                    value={settings.video_model || 'grok-imagine-video'}
                    label="Модель оживления видео"
                    onChange={(e) => updateSetting('video_model', e.target.value)}
                  >
                    <MenuItem value="grok-imagine-video">
                      <Box>
                        <Typography>Grok Imagine Video</Typography>
                        <Typography variant="caption" color="textSecondary">
                          Сохраняет точный размер фото, без водяного знака
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="veo-3.1">
                      <Box>
                        <Typography>Veo 3.1</Typography>
                        <Typography variant="caption" color="textSecondary">
                          720p, берёт ваше фото; со звуком
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="gemini-omni">
                      <Box>
                        <Typography>Gemini Omni</Typography>
                        <Typography variant="caption" color="textSecondary">
                          Всегда 720p, 8 сек; иногда требует пары попыток
                        </Typography>
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <Alert severity="info" sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                  «Сохранить» меняет только модель видео — остальные настройки Jarvis зашиты и не трогаются. Выбор подхватится за ~5 сек. Veo/Omni несут мелкий водяной знак «Veo».
                </Alert>
              </Grid>
            </Grid>

            {/* ── Демо-сутки новичку ── */}
            <Divider sx={{ my: 4 }} />
            <Typography variant="h6" gutterBottom>
              Демо-доступ новичкам
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.demo_enabled !== 'false'}
                      onChange={(e) => updateSetting('demo_enabled', e.target.checked ? 'true' : 'false')}
                      color="success"
                    />
                  }
                  label="Дарить доступ при первом сообщении"
                />
                <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 2 }}>
                  Человек пишет Джарвису впервые — получает поздравление и
                  полный доступ. Подарок выдаётся один раз в жизни
                </Typography>

                <TextField
                  fullWidth
                  type="number"
                  label="Сколько суток длится подарок"
                  value={settings.demo_days || '1'}
                  onChange={(e) => updateSetting('demo_days', e.target.value)}
                  inputProps={{ min: 1, max: 30 }}
                  helperText="По умолчанию сутки"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Alert severity="warning">
                  Видео в подарок НЕ входит — оживление фото и ролики
                  открываются только после оплаты. Всё остальное доступно
                  полностью: картинки, песни, подкасты, презентации,
                  YouTube, поиск, голос.
                </Alert>
              </Grid>
            </Grid>

            {/* ── Музыка: песни через Suno (kie.ai) ── */}
            <Divider sx={{ my: 4 }} />
            <Typography variant="h6" gutterBottom>
              Музыка — песни через Suno
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.music_enabled !== 'false'}
                      onChange={(e) => updateSetting('music_enabled', e.target.checked ? 'true' : 'false')}
                      color="success"
                    />
                  }
                  label="Создание песен включено"
                />
                <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 2 }}>
                  Выключено — Джарвис вежливо отказывает и ничего не тратит
                </Typography>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Модель Suno</InputLabel>
                  <Select
                    value={settings.music_model || 'V6'}
                    label="Модель Suno"
                    onChange={(e) => updateSetting('music_model', e.target.value)}
                  >
                    <MenuItem value="V6">
                      <Box>
                        <Typography>V6 (боевая)</Typography>
                        <Typography variant="caption" color="textSecondary">
                          Живее вокал и больше деталей, трек до 8 минут
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="V6_WILD">
                      <Box>
                        <Typography>V6 Wild</Typography>
                        <Typography variant="caption" color="textSecondary">
                          Смелее и неожиданнее, мешает жанры
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="V6_MINI">
                      <Box>
                        <Typography>V6 Mini</Typography>
                        <Typography variant="caption" color="textSecondary">
                          Быстрая и лёгкая, качество чуть проще
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="V5_5">
                      <Box>
                        <Typography>V5.5 (старая)</Typography>
                        <Typography variant="caption" color="textSecondary">
                          Прежняя боевая, у kie помечена снятой с поддержки
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="V5">
                      <Box>
                        <Typography>V5</Typography>
                        <Typography variant="caption" color="textSecondary">
                          Чуть быстрее, слабее по деталям
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="V4_5PLUS">
                      <Box>
                        <Typography>V4.5+</Typography>
                        <Typography variant="caption" color="textSecondary">
                          Трек до 8 минут, звук проще
                        </Typography>
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  type="number"
                  label="Лимит песен в сутки (оплатившие)"
                  value={settings.music_daily_limit || '5'}
                  onChange={(e) => updateSetting('music_daily_limit', e.target.value)}
                  inputProps={{ min: 0, max: 50 }}
                  helperText="Сброс в 00:00 МСК. Админы без лимита"
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  type="number"
                  label="Лимит песен в сутки (демо-доступ)"
                  value={settings.music_demo_limit || '3'}
                  onChange={(e) => updateSetting('music_demo_limit', e.target.value)}
                  inputProps={{ min: 0, max: 20 }}
                  helperText="Для подаренных суток — меньше, чем у оплативших"
                  sx={{ mb: 2 }}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.music_video_enabled !== 'false'}
                      onChange={(e) => updateSetting('music_video_enabled', e.target.checked ? 'true' : 'false')}
                      color="success"
                    />
                  }
                  label="Присылать клипы к трекам"
                />
                <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 2 }}>
                  Вертикальный ролик с обложкой и караоке, +$0.01 к треку
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.music_wav_enabled === 'true'}
                      onChange={(e) => updateSetting('music_wav_enabled', e.target.checked ? 'true' : 'false')}
                      color="success"
                    />
                  }
                  label="Досылать WAV без сжатия"
                />
                <Typography variant="caption" color="textSecondary" display="block">
                  +$0.002 к треку, файл ~39 МБ. Верх спектра тот же, что у
                  mp3 — нужен для монтажа, а не «чтобы звучало лучше»
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Модели для текста песни (через запятую)"
                  value={settings.music_lyrics_models || 'gpt-5.6-sol,gemini-3.1-pro-preview,gpt-5.6-luna'}
                  onChange={(e) => updateSetting('music_lyrics_models', e.target.value)}
                  helperText="Первая пишет текст, остальные подхватывают, если она молчит"
                  sx={{ mb: 2 }}
                />
                <Alert severity="info">
                  Один запрос к Suno — это ДВА варианта трека, 12 кредитов
                  kie.ai (~$0.06), цена от версии модели не зависит. Оба
                  варианта доезжают примерно за минуту-полторы. Доступ к
                  песням тот же, что к видео: оплаченная подписка, вайтлист
                  и админы. Только в личке.
                </Alert>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        {/* Промпты — вкладка скрыта (правим в коде), панель недостижима */}
        <TabPanel value={tabValue} index={90}>
          <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
            <Typography variant="h6" gutterBottom>
              Системный промпт Level 1 (базовый)
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={15}
              value={settings.system_prompt_level1 || ''}
              onChange={(e) => updateSetting('system_prompt_level1', e.target.value)}
              sx={{ mb: 3, fontFamily: 'monospace' }}
            />

            <Typography variant="h6" gutterBottom>
              Дополнение для Level 2 (бизнес-тренер)
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={10}
              value={settings.system_prompt_level2_addition || ''}
              onChange={(e) => updateSetting('system_prompt_level2_addition', e.target.value)}
              sx={{ fontFamily: 'monospace' }}
            />
          </Box>
        </TabPanel>

        {/* База знаний */}
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 3 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MenuBook /> База знаний Jarvis
                {knowledgeStats && (
                  <Chip label={`${knowledgeStats.total} записей`} size="small" color="primary" />
                )}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<Sync />}
                  onClick={syncKnowledge}
                >
                  Синхронизировать с RAG
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={loadKnowledge}
                  disabled={knowledgeLoading}
                >
                  Обновить
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => openKnowledgeDialog()}
                >
                  Добавить знание
                </Button>
              </Box>
            </Box>

            {/* Статистика */}
            {knowledgeStats && (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {knowledgeStats.by_category?.map((cat) => (
                  <Grid item key={cat.category}>
                    <Chip
                      label={`${cat.category}: ${cat.count}`}
                      sx={{ bgcolor: getCategoryColor(cat.category), color: '#fff' }}
                      onClick={() => {
                        setKnowledgeFilter(cat.category);
                        loadKnowledge();
                      }}
                    />
                  </Grid>
                ))}
                <Grid item>
                  <Chip
                    label="Все"
                    variant={knowledgeFilter === 'all' ? 'filled' : 'outlined'}
                    onClick={() => {
                      setKnowledgeFilter('all');
                      loadKnowledge();
                    }}
                  />
                </Grid>
              </Grid>
            )}

            {/* Поиск и фильтры */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <TextField
                size="small"
                placeholder="Поиск по тексту..."
                value={knowledgeSearch}
                onChange={(e) => setKnowledgeSearch(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && loadKnowledge()}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                sx={{ flexGrow: 1 }}
              />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Категория</InputLabel>
                <Select
                  value={knowledgeFilter}
                  label="Категория"
                  onChange={(e) => {
                    setKnowledgeFilter(e.target.value);
                    setTimeout(loadKnowledge, 100);
                  }}
                >
                  <MenuItem value="all">Все категории</MenuItem>
                  {knowledgeCategories.map((cat) => (
                    <MenuItem key={cat.category} value={cat.category}>
                      {cat.category} ({cat.count})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="outlined" onClick={loadKnowledge}>
                Искать
              </Button>
            </Box>

            {/* Таблица знаний */}
            {knowledgeLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ bgcolor: 'rgba(255,255,255,0.02)', maxHeight: 600 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width="100">ID</TableCell>
                      <TableCell width="120">Категория</TableCell>
                      <TableCell>Текст</TableCell>
                      <TableCell width="100">Источник</TableCell>
                      <TableCell width="150">Дата</TableCell>
                      <TableCell width="100" align="center">Действия</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {knowledge.map((item) => (
                      <TableRow key={item.id} hover>
                        <TableCell>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                            {item.doc_id?.substring(0, 15)}...
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={item.category}
                            size="small"
                            sx={{ bgcolor: getCategoryColor(item.category), color: '#fff' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{
                            maxWidth: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {item.text}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={item.source} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {new Date(item.created_at).toLocaleString('ru-RU')}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Редактировать">
                            <IconButton size="small" onClick={() => openKnowledgeDialog(item)}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Удалить">
                            <IconButton size="small" color="error" onClick={() => deleteKnowledge(item.id)}>
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                    {knowledge.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          Нет записей в базе знаний
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Alert severity="info" sx={{ mt: 2 }}>
              После добавления/редактирования знаний нажмите "Синхронизировать с RAG" для обновления векторной базы поиска Jarvis.
            </Alert>
          </Box>
        </TabPanel>

        {/* Диалоги — вкладка скрыта, панель недостижима */}
        <TabPanel value={tabValue} index={91}>
          <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
            <Typography variant="h6" gutterBottom>
              История диалогов
            </Typography>
            <TableContainer component={Paper} sx={{ bgcolor: 'rgba(255,255,255,0.02)' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Пользователь</TableCell>
                    <TableCell>Telegram ID</TableCell>
                    <TableCell align="right">Сообщений</TableCell>
                    <TableCell>Начало</TableCell>
                    <TableCell>Последнее</TableCell>
                    <TableCell align="center">Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {conversations.map((conv) => (
                    <TableRow key={conv.id}>
                      <TableCell>
                        {conv.first_name || conv.username || 'Без имени'}
                      </TableCell>
                      <TableCell>{conv.telegram_id}</TableCell>
                      <TableCell align="right">{conv.messages_count}</TableCell>
                      <TableCell>
                        {new Date(conv.started_at).toLocaleString('ru-RU')}
                      </TableCell>
                      <TableCell>
                        {new Date(conv.last_message_at).toLocaleString('ru-RU')}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleViewConversation(conv)}
                        >
                          <Visibility />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {conversations.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        Нет диалогов
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </TabPanel>

        {/* Рассылки */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Рассылки
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <IconButton size="small" onClick={loadBroadcasts} disabled={broadcastsLoading}>
                  <Refresh />
                </IconButton>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Add />}
                  onClick={() => openBroadcastDialog()}
                  sx={{ fontSize: '0.75rem', py: 0.5 }}
                >
                  Создать
                </Button>
              </Box>
            </Box>

            <Alert severity="info" sx={{ mb: 2, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>
              {recipientsCount || '...'} диалогов. 1 запрос AI = 6-8 вариаций. Delay 25-45 сек.
            </Alert>

            {/* Live-прогресс активной рассылки */}
            {liveStatus && liveStatus.running && (
              <Paper sx={{ p: 2, mb: 3, bgcolor: 'rgba(76, 175, 80, 0.08)', border: '1px solid rgba(76, 175, 80, 0.3)' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold" color="success.main">
                    Рассылка активна: {liveStatus.title || `#${liveStatus.broadcast_id}`}
                  </Typography>
                  <Chip
                    label={liveStatus.flood?.blocked ? `БЛОК до ${liveStatus.flood.until}` : liveStatus.paused ? 'ПАУЗА' : 'ОТПРАВКА...'}
                    color={liveStatus.flood?.blocked ? 'error' : liveStatus.paused ? 'warning' : 'success'}
                    size="small"
                  />
                </Box>
                <Box sx={{ width: '100%', mb: 1 }}>
                  <Box sx={{
                    height: 8, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.1)',
                    overflow: 'hidden', position: 'relative'
                  }}>
                    <Box sx={{
                      height: '100%', borderRadius: 4,
                      bgcolor: liveStatus.paused ? 'warning.main' : 'success.main',
                      width: `${liveStatus.target_count ? Math.round((liveStatus.total_processed / liveStatus.target_count) * 100) : 0}%`,
                      transition: 'width 0.5s ease'
                    }} />
                  </Box>
                </Box>
                {liveStatus.flood?.blocked && (
                  <Alert severity="error" sx={{ mb: 1 }}>
                    {liveStatus.flood.reason} — блок до <b>{liveStatus.flood.until}</b> (осталось ~{liveStatus.flood.remaining_min} мин)
                  </Alert>
                )}
                <Box sx={{ display: 'flex', gap: 3 }}>
                  <Typography variant="body2">
                    Отправлено: <b>{liveStatus.delivered || 0}</b> / {liveStatus.target_count || 0}
                  </Typography>
                  <Typography variant="body2" color="error.main">
                    Ошибок: {liveStatus.failed || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Пропущено: {liveStatus.skipped || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Прогресс: {liveStatus.target_count ? Math.round((liveStatus.total_processed / liveStatus.target_count) * 100) : 0}%
                  </Typography>
                </Box>
              </Paper>
            )}

            {broadcastsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ bgcolor: 'rgba(255,255,255,0.02)' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Название</TableCell>
                      <TableCell>Статус</TableCell>
                      <TableCell>Стиль</TableCell>
                      <TableCell align="right">Доставлено</TableCell>
                      <TableCell align="right">Успешно</TableCell>
                      <TableCell>Создана</TableCell>
                      <TableCell align="center">Действия</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {broadcasts.map((broadcast) => (
                      <TableRow key={broadcast.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {broadcast.title}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {broadcast.message_hint?.substring(0, 50)}...
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getBroadcastStatusLabel(broadcast.status)}
                            color={getBroadcastStatusColor(broadcast.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={broadcast.delivery_style === 'soft' ? 'Мягкий' :
                                   broadcast.delivery_style === 'direct' ? 'Прямой' : 'Срочный'}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right">
                          {broadcast.delivered_count || broadcast.sent_count || 0}
                          {broadcast.target_count ? ` / ${broadcast.target_count}` : ''}
                        </TableCell>
                        <TableCell align="right">
                          <Typography color="success.main">
                            {broadcast.delivered_count || broadcast.sent_count || 0}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {new Date(broadcast.created_at).toLocaleString('ru-RU')}
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                            {broadcast.status === 'draft' && (
                              <>
                                <Tooltip title="Запустить">
                                  <IconButton size="small" color="success" onClick={() => startBroadcast(broadcast.id)}>
                                    <PlayArrow />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Редактировать">
                                  <IconButton size="small" onClick={() => openBroadcastDialog(broadcast)}>
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                            {broadcast.status === 'active' && (
                              <>
                                <Tooltip title="Возобновить (после рестарта)">
                                  <IconButton size="small" color="success" onClick={() => resumeBroadcast(broadcast.id)}>
                                    <PlayArrow />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Смотреть прогресс">
                                  <IconButton size="small" color="primary" onClick={() => startLivePolling(broadcast.id)}>
                                    <Visibility />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Приостановить">
                                  <IconButton size="small" color="warning" onClick={() => pauseBroadcast(broadcast.id)}>
                                    <Pause />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Завершить">
                                  <IconButton size="small" color="info" onClick={() => completeBroadcast(broadcast.id)}>
                                    <Stop />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                            {broadcast.status === 'paused' && (
                              <>
                                <Tooltip title="Возобновить">
                                  <IconButton size="small" color="success" onClick={() => resumeBroadcast(broadcast.id)}>
                                    <PlayArrow />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Завершить">
                                  <IconButton size="small" color="info" onClick={() => completeBroadcast(broadcast.id)}>
                                    <Stop />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                            <Tooltip title="Удалить">
                              <IconButton size="small" color="error" onClick={() => deleteBroadcast(broadcast.id)}>
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                    {broadcasts.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          Нет рассылок. Создайте первую!
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </TabPanel>

        {/* Групповой чат — вкладка скрыта, панель недостижима */}
        <TabPanel value={tabValue} index={92}>
          <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 3 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Forum /> Настройки группового чата
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={loadGroupChatSettings}
                  disabled={groupChatLoading}
                >
                  Обновить
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={saveGroupChatSettings}
                >
                  Сохранить
                </Button>
              </Box>
            </Box>

            {/* ГЛАВНЫЙ РУБИЛЬНИК: Джарвис пишет в группы или молчит */}
            <Card
              sx={{
                mb: 3,
                border: '2px solid',
                borderColor: groupChatSettings.group_publications_enabled !== 'true' ? '#f44336' : '#4CAF50',
                bgcolor: groupChatSettings.group_publications_enabled !== 'true' ? 'rgba(244,67,54,0.08)' : 'rgba(76,175,80,0.08)'
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {groupChatSettings.group_publications_enabled !== 'true'
                      ? <VolumeOff sx={{ fontSize: 40, color: '#f44336' }} />
                      : <VolumeUp sx={{ fontSize: 40, color: '#4CAF50' }} />}
                    <Box>
                      <Typography variant="h6">
                        {groupChatSettings.group_publications_enabled !== 'true'
                          ? 'Джарвис МОЛЧИТ в группах'
                          : 'Джарвис ПИШЕТ в группах'}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Один рубильник на всё: саммари дня, «чат закрыт/открыт», утренняя
                        картинка, ночной комикс, провокации при тишине, периодические посты,
                        ответы на упоминания, игры, комиксы, стикеры и приветствия новичков.
                      </Typography>
                    </Box>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        color="success"
                        checked={groupChatSettings.group_publications_enabled === 'true'}
                        onChange={(e) => toggleGroupPublications(e.target.checked)}
                      />
                    }
                    label={groupChatSettings.group_publications_enabled !== 'true' ? 'Включить' : 'Выключить'}
                  />
                </Box>
                <Alert severity="info" sx={{ mt: 2 }}>
                  Модерация не отключается: удаление ссылок и спама, муты, баны, антиспам и
                  авто-закрытие чата по расписанию работают как обычно — просто молча.
                  Личка Джарвиса рубильник не трогает. Применяется без рестарта, в течение 5 секунд.
                </Alert>
              </CardContent>
            </Card>

            {groupChatLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {/* Статистика */}
                {groupChatStats && (
                  <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={3}>
                      <StatCard
                        title="Сообщений сегодня"
                        value={groupChatStats.messages_today || 0}
                        icon={<Message sx={{ fontSize: 40 }} />}
                        color="#2196F3"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <StatCard
                        title="Реакций"
                        value={groupChatStats.reactions_sent || 0}
                        icon={<EmojiEmotions sx={{ fontSize: 40 }} />}
                        color="#FF9800"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <StatCard
                        title="Игр сыграно"
                        value={groupChatStats.games_played || 0}
                        icon={<SportsEsports sx={{ fontSize: 40 }} />}
                        color="#4CAF50"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <StatCard
                        title="Удалено ссылок"
                        value={groupChatStats.links_deleted || 0}
                        icon={<Security sx={{ fontSize: 40 }} />}
                        color="#f44336"
                      />
                    </Grid>
                  </Grid>
                )}

                <Divider sx={{ my: 3 }} />

                {/* Основные настройки */}
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Settings /> Основные настройки
                </Typography>
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={groupChatSettings.group_welcome_enabled === 'true'}
                          onChange={(e) => updateGroupChatSetting('group_welcome_enabled', e.target.checked ? 'true' : 'false')}
                        />
                      }
                      label="Приветствие новичков"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={groupChatSettings.group_reactions_enabled === 'true'}
                          onChange={(e) => updateGroupChatSetting('group_reactions_enabled', e.target.checked ? 'true' : 'false')}
                        />
                      }
                      label="Реакции на сообщения"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={groupChatSettings.group_moderation_enabled === 'true'}
                          onChange={(e) => updateGroupChatSetting('group_moderation_enabled', e.target.checked ? 'true' : 'false')}
                        />
                      }
                      label="Модерация ссылок"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={groupChatSettings.group_games_enabled === 'true'}
                          onChange={(e) => updateGroupChatSetting('group_games_enabled', e.target.checked ? 'true' : 'false')}
                        />
                      }
                      label="Мини-игры"
                    />
                  </Grid>
                </Grid>

                {/* Вероятность реакций */}
                <Box sx={{ mb: 3 }}>
                  <Typography gutterBottom>
                    Вероятность реакций: <strong>{groupChatSettings.group_reactions_probability || 10}%</strong>
                  </Typography>
                  <Slider
                    value={parseInt(groupChatSettings.group_reactions_probability) || 10}
                    onChange={(e, value) => updateGroupChatSetting('group_reactions_probability', value.toString())}
                    min={0}
                    max={50}
                    step={5}
                    marks={[
                      { value: 0, label: '0%' },
                      { value: 10, label: '10%' },
                      { value: 25, label: '25%' },
                      { value: 50, label: '50%' }
                    ]}
                    valueLabelDisplay="auto"
                    sx={{ maxWidth: 400 }}
                  />
                  <Typography variant="caption" color="textSecondary">
                    Процент сообщений, на которые бот будет ставить реакции
                  </Typography>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Авто-закрытие чата */}
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Schedule /> Авто-закрытие чата
                </Typography>
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={groupChatSettings.group_auto_close_enabled === 'true'}
                          onChange={(e) => updateGroupChatSetting('group_auto_close_enabled', e.target.checked ? 'true' : 'false')}
                        />
                      }
                      label="Авто-закрытие/открытие по расписанию"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Время закрытия (МСК)"
                      placeholder="02:00"
                      value={groupChatSettings.group_close_time || '02:00'}
                      onChange={(e) => updateGroupChatSetting('group_close_time', e.target.value)}
                      helperText="Формат: HH:MM"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Время открытия (МСК)"
                      placeholder="08:00"
                      value={groupChatSettings.group_open_time || '08:00'}
                      onChange={(e) => updateGroupChatSetting('group_open_time', e.target.value)}
                      helperText="Формат: HH:MM"
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* Разрешённые ссылки */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LinkIcon /> Разрешённые ссылки
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={() => setDomainDialogOpen(true)}
                  >
                    Добавить
                  </Button>
                </Box>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Все ссылки, кроме указанных ниже, будут автоматически удалены и пользователь получит предупреждение.
                </Alert>
                <TableContainer component={Paper} sx={{ bgcolor: 'rgba(255,255,255,0.02)' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Разрешённая ссылка</TableCell>
                        <TableCell>Описание</TableCell>
                        <TableCell width="80" align="center">Действия</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {allowedDomains.map((domain) => (
                        <TableRow key={domain.id}>
                          <TableCell>
                            <Typography sx={{ fontFamily: 'monospace', color: '#4CAF50' }}>
                              {domain.domain}
                            </Typography>
                          </TableCell>
                          <TableCell>{domain.description}</TableCell>
                          <TableCell align="center">
                            <IconButton size="small" color="error" onClick={() => deleteAllowedDomain(domain.id)}>
                              <Delete />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                      {allowedDomains.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={3} align="center">
                            Нет разрешённых ссылок
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Divider sx={{ my: 3 }} />

                {/* Мини-игры */}
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SportsEsports /> Мини-игры в чате
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Пользователи могут вызвать игры командами: <strong>/coinflip</strong>, <strong>/dice</strong>, <strong>/8ball</strong>, <strong>/slot</strong>, <strong>/fortune</strong>, <strong>/trivia</strong>.
                  Также доступны: <strong>"комикс"</strong> для генерации комиксов NanoBanana.
                </Alert>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Card sx={{ bgcolor: 'rgba(255,255,255,0.05)' }}>
                      <CardContent>
                        <Typography variant="subtitle2">Coinflip</Typography>
                        <Typography variant="caption" color="textSecondary">Орёл или решка</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Card sx={{ bgcolor: 'rgba(255,255,255,0.05)' }}>
                      <CardContent>
                        <Typography variant="subtitle2">Dice</Typography>
                        <Typography variant="caption" color="textSecondary">Бросок кубика</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Card sx={{ bgcolor: 'rgba(255,255,255,0.05)' }}>
                      <CardContent>
                        <Typography variant="subtitle2">8ball</Typography>
                        <Typography variant="caption" color="textSecondary">Магический шар</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Card sx={{ bgcolor: 'rgba(255,255,255,0.05)' }}>
                      <CardContent>
                        <Typography variant="subtitle2">Fortune</Typography>
                        <Typography variant="caption" color="textSecondary">Предсказание</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Card sx={{ bgcolor: 'rgba(255,255,255,0.05)' }}>
                      <CardContent>
                        <Typography variant="subtitle2">Slot</Typography>
                        <Typography variant="caption" color="textSecondary">Слот-машина</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Card sx={{ bgcolor: 'rgba(255,255,255,0.05)' }}>
                      <CardContent>
                        <Typography variant="subtitle2">Trivia</Typography>
                        <Typography variant="caption" color="textSecondary">Викторина</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Card sx={{ bgcolor: 'rgba(255,215,0,0.1)' }}>
                      <CardContent>
                        <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Celebration fontSize="small" /> Комикс
                        </Typography>
                        <Typography variant="caption" color="textSecondary">NanoBanana комикс</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </>
            )}
          </Box>
        </TabPanel>

        {/* Level 2 */}
        <TabPanel value={tabValue} index={4}>
          <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 3 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Star /> Принудительный Level 2
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={loadForcedLevel2}
                  disabled={forcedLevel2Loading}
                >
                  Обновить
                </Button>
                <Button
                  variant="contained"
                  startIcon={<PersonAdd />}
                  onClick={() => setForcedLevel2Dialog(true)}
                >
                  Выдать Level 2
                </Button>
              </Box>
            </Box>

            <Alert severity="info" sx={{ mb: 3 }}>
              Принудительный Level 2 даёт пользователю расширенный промпт Jarvis
              вне зависимости от стейкинга и рефералов. Кэш обновляется в течение 60 секунд.
            </Alert>

            {forcedLevel2Loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Telegram ID</TableCell>
                      <TableCell>Username</TableCell>
                      <TableCell>Баланс</TableCell>
                      <TableCell>Заметка</TableCell>
                      <TableCell>Дата выдачи</TableCell>
                      <TableCell>Действия</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {forcedLevel2Users.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                          Нет пользователей с принудительным Level 2
                        </TableCell>
                      </TableRow>
                    ) : (
                      forcedLevel2Users.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {user.telegram_id}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {user.current_username || user.username || '—'}
                          </TableCell>
                          <TableCell>
                            {user.balance != null ? `${Number(user.balance).toFixed(2)} SPERM` : '—'}
                          </TableCell>
                          <TableCell>{user.note || '—'}</TableCell>
                          <TableCell>
                            {user.created_at ? new Date(user.created_at).toLocaleDateString('ru-RU') : '—'}
                          </TableCell>
                          <TableCell>
                            <Tooltip title="Отозвать Level 2">
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() => revokeForcedLevel2(user)}
                              >
                                <PersonRemove />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Поимённый доступ к видео */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 5, mb: 2 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                🎬 Доступ к видео
              </Typography>
              <Button
                variant="contained"
                startIcon={<PersonAdd />}
                onClick={() => setVideoAccessDialog(true)}
              >
                Открыть видео
              </Button>
            </Box>

            <Alert severity="warning" sx={{ mb: 2 }}>
              Видео (ролики и оживление фото) открывает <b>оплаченная подписка</b> — такие
              юзеры проходят автоматически, вписывать их сюда не нужно. Этот список —
              для тех, кому даём видео <b>без оплаты</b>. Админы проходят без записи.
              Level 2 без оплаты (демо-сутки, триал, ручная выдача) видео НЕ открывает:
              таким юзерам Jarvis отвечает, что видео доступно после оплаты премиума.
              Лимит у тех, кому открыто, — 5 видео в сутки. Подхват изменений — 60 секунд.
            </Alert>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Telegram ID</TableCell>
                    <TableCell>Username</TableCell>
                    <TableCell>Сегодня</TableCell>
                    <TableCell>Заметка</TableCell>
                    <TableCell>Открыт</TableCell>
                    <TableCell>Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {videoAccess.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                        Список пуст — видео работает у оплативших подписку и админов
                      </TableCell>
                    </TableRow>
                  ) : (
                    videoAccess.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {row.telegram_id || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>{row.current_username || row.username || '—'}</TableCell>
                        <TableCell>{row.telegram_id ? `${row.used_today} / 5` : '—'}</TableCell>
                        <TableCell>{row.note || '—'}</TableCell>
                        <TableCell>
                          {row.created_at ? new Date(row.created_at).toLocaleDateString('ru-RU') : '—'}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Закрыть доступ к видео">
                            <IconButton color="error" size="small" onClick={() => removeVideoAccess(row)}>
                              <PersonRemove />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Индивидуальные лимиты генераций картинок */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 5, mb: 2 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                🖼️ Лимиты картинок
              </Typography>
              <Button
                variant="contained"
                startIcon={<PersonAdd />}
                onClick={() => setImageLimitDialog(true)}
              >
                Задать лимит
              </Button>
            </Box>

            <Alert severity="info" sx={{ mb: 2 }}>
              Индивидуальный дневной лимит генераций изображений в Jarvis.
              Без записи здесь действует дефолт 20/день. Сброс счётчика — 00:00 МСК.
              Jarvis подхватывает изменения в течение 60 секунд.
            </Alert>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Telegram ID</TableCell>
                    <TableCell>Username</TableCell>
                    <TableCell>Лимит/день</TableCell>
                    <TableCell>Сегодня</TableCell>
                    <TableCell>Заметка</TableCell>
                    <TableCell>Обновлён</TableCell>
                    <TableCell>Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {imageLimits.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                        Индивидуальных лимитов нет — у всех дефолт 15/день
                      </TableCell>
                    </TableRow>
                  ) : (
                    imageLimits.map((row) => (
                      <TableRow key={row.telegram_id}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {row.telegram_id}
                          </Typography>
                        </TableCell>
                        <TableCell>{row.current_username || row.username || '—'}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {row.daily_limit}
                          </Typography>
                        </TableCell>
                        <TableCell>{row.used_today} / {row.daily_limit}</TableCell>
                        <TableCell>{row.note || '—'}</TableCell>
                        <TableCell>
                          {row.updated_at ? new Date(row.updated_at).toLocaleDateString('ru-RU') : '—'}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Изменить лимит">
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => {
                                setImageLimitForm({
                                  identifier: String(row.telegram_id),
                                  limit: String(row.daily_limit),
                                  note: row.note || '',
                                });
                                setImageLimitDialog(true);
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Убрать лимит (вернуть дефолт)">
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => removeImageLimit(row)}
                            >
                              <PersonRemove />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </TabPanel>

        {/* 💎 Платёжка Jarvis Premium */}
        <TabPanel value={tabValue} index={5}>
          <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Payments /> Платёжка Jarvis Premium
              </Typography>
              <Button size="small" startIcon={<Refresh />} onClick={loadPremium} disabled={premiumLoading}>
                Обновить
              </Button>
            </Box>

            {/* Стат-карточки */}
            {premiumStats && (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {[
                  { label: 'Выручка', value: `$${premiumStats.revenue.toFixed(2)}`, sub: `${premiumStats.monthsSold} мес продано` },
                  { label: 'Активных подписок', value: premiumStats.subsActive, sub: `всего платили: ${premiumStats.subsTotal}` },
                  { label: 'Платежей', value: premiumStats.payCount, sub: `на $${premiumStats.paySum.toFixed(2)}` },
                  { label: 'За сегодня', value: `$${premiumStats.payTodaySum.toFixed(2)}`, sub: `${premiumStats.payTodayCount} шт · 7д: $${premiumStats.pay7Sum.toFixed(2)}` },
                  { label: 'Подарков оплачено', value: premiumStats.giftsPaid, sub: `на $${premiumStats.giftsSum.toFixed(2)}` },
                  { label: 'Кошельков выдано', value: premiumStats.wallets, sub: `истекают ≤3д: ${premiumStats.expiring}` },
                  { label: 'Не собрано (BEP-20)',
                    value: (liveUncollected ? liveUncollected.bsc : premiumStats.uncollectedBsc).toFixed(2),
                    sub: liveUncollected ? `USDT на адресах · на ${treasury.fetchedAt}` : 'USDT · по базе, жду блокчейн' },
                  { label: 'Не собрано (TON)',
                    value: (liveUncollected ? liveUncollected.ton : premiumStats.uncollectedTon).toFixed(2),
                    sub: liveUncollected ? `USDT на адресах · на ${treasury.fetchedAt}` : 'USDT · по базе, жду блокчейн' },
                ].map((c, i) => (
                  <Grid item xs={6} md={3} key={i}>
                    <Paper sx={{ p: 1.5, bgcolor: 'rgba(255,255,255,0.03)', height: '100%' }}>
                      <Typography variant="caption" color="text.secondary">{c.label}</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>{c.value}</Typography>
                      <Typography variant="caption" color="text.secondary">{c.sub}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}

            {/* 🏦 Казна: сборные кошельки, вывод, газ */}
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'rgba(255,255,255,0.03)' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                <Typography variant="subtitle2">
                  🏦 Казна (живые он-чейн балансы)
                  {treasury?.fetchedAt && (
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      данные на {treasury.fetchedAt}
                    </Typography>
                  )}
                </Typography>
                <Button size="small" startIcon={treasuryLoading ? <CircularProgress size={14} /> : <Refresh />}
                  onClick={loadTreasury} disabled={treasuryLoading || !!treasuryBusy}>
                  Обновить балансы
                </Button>
              </Box>

              {!treasury && (
                <Typography variant="body2" color="text.secondary">
                  {treasuryLoading ? 'Опрашиваю блокчейны…' : 'Балансы ещё не загружены'}
                </Typography>
              )}

              {treasury && (
                <>
                  {/* Сборные кошельки */}
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    {[
                      {
                        title: 'Сборный BEP-20 (BSC)', address: treasury.main.bsc.address,
                        usdt: treasury.main.bsc.usdt, gas: treasury.main.bsc.bnb,
                        gasCoin: 'BNB', gasOk: treasury.main.bsc.gasOk,
                      },
                      {
                        title: 'Сборный TON', address: treasury.main.ton.address,
                        usdt: treasury.main.ton.usdt, gas: treasury.main.ton.ton,
                        gasCoin: 'TON', gasOk: treasury.main.ton.gasOk,
                      },
                    ].map((w, i) => (
                      <Grid item xs={12} md={6} key={i}>
                        <Paper sx={{ p: 1.5, bgcolor: 'rgba(255,255,255,0.04)', height: '100%' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" color="text.secondary">{w.title}</Typography>
                            <Chip size="small" variant="outlined"
                              label={w.gasOk == null ? '⛽ н/д'
                                : (w.gasOk ? '⛽ газ ок' : `⛽ пополни ${w.gasCoin}!`)}
                              color={w.gasOk == null ? 'default' : (w.gasOk ? 'success' : 'warning')} />
                          </Box>
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {fmtBal(w.usdt, 2)} USDT
                            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                              · {fmtBal(w.gas, w.gasCoin === 'BNB' ? 5 : 3)} {w.gasCoin}
                            </Typography>
                          </Typography>
                          <Typography sx={{ fontSize: '0.65rem', fontFamily: 'monospace', wordBreak: 'break-all' }}
                            onClick={() => { navigator.clipboard?.writeText(w.address); setMessage({ type: 'success', text: 'Адрес скопирован' }); }}>
                            {w.address}
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    Газ для сборов берётся со сборных кошельков: BNB — на автосбор BEP-20, TON — на сбор
                    USDT-TON с адресов юзеров (дозаправка автоматом при сборе). Пополняются обычным переводом на адрес выше.
                  </Typography>

                  {/* Отправка со сборного кошелька */}
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>📤 Отправить со сборного</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
                    <FormControl size="small" sx={{ width: 110 }}>
                      <InputLabel>Сеть</InputLabel>
                      <Select value={sendForm.network} label="Сеть"
                        onChange={(e) => setSendForm({ ...sendForm, network: e.target.value })}>
                        <MenuItem value="bsc">BEP-20</MenuItem>
                        <MenuItem value="ton">TON</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ width: 110 }}>
                      <InputLabel>Актив</InputLabel>
                      <Select value={sendForm.asset} label="Актив"
                        onChange={(e) => setSendForm({ ...sendForm, asset: e.target.value })}>
                        <MenuItem value="usdt">USDT</MenuItem>
                        <MenuItem value="native">{sendForm.network === 'bsc' ? 'BNB' : 'TON'}</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField size="small" label="Адрес получателя" value={sendForm.to}
                      onChange={(e) => setSendForm({ ...sendForm, to: e.target.value })}
                      sx={{ flex: '1 1 240px' }} />
                    <TextField size="small" label="Сумма (пусто = всё)" value={sendForm.amount}
                      onChange={(e) => setSendForm({ ...sendForm, amount: e.target.value })}
                      sx={{ width: 150 }} />
                    <Button variant="contained" color="warning" onClick={sendFromMain}
                      disabled={!!treasuryBusy}>
                      {treasuryBusy === 'send' ? 'Отправляю…' : 'Отправить'}
                    </Button>
                  </Box>

                  {/* Адреса юзеров с живыми балансами и сбором */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                    <Typography variant="subtitle2">👛 Адреса юзеров</Typography>
                    <Button size="small" variant="contained" color="success"
                      disabled={!!treasuryBusy || !liveUncollected?.count}
                      onClick={startSweepAll}>
                      {treasuryBusy === 'sweep-all'
                        ? `Собираю ${sweepAll?.done ?? 0}/${sweepAll?.total ?? '…'}…`
                        : `Собрать все${liveUncollected?.count ? ` (${(liveUncollected.bsc + liveUncollected.ton).toFixed(2)} USDT)` : ''}`}
                    </Button>
                  </Box>
                  {treasuryBusy === 'sweep-all' && sweepAll?.current && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Сейчас: {sweepAll.current} · собрано BEP-20 {(sweepAll.collected?.bsc || 0).toFixed(2)},
                      TON {(sweepAll.collected?.ton || 0).toFixed(2)} USDT
                    </Typography>
                  )}
                  <TableContainer>
                    <Table size="small">
                      <TableHead><TableRow>
                        <TableCell>Юзер</TableCell><TableCell>Сеть</TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Адрес</TableCell>
                        <TableCell align="right">USDT</TableCell>
                        <TableCell align="right">Газ</TableCell>
                        <TableCell align="right">Действия</TableCell>
                      </TableRow></TableHead>
                      <TableBody>
                        {treasury.users.flatMap((u) => ([
                          { ...u, network: 'bsc', net: 'BEP-20', address: u.bscAddress, usdt: u.bscUsdt, gas: u.bscBnb, gasCoin: 'BNB' },
                          { ...u, network: 'ton', net: 'TON', address: u.tonAddress, usdt: u.tonUsdt, gas: u.tonGas, gasCoin: 'TON' },
                        // несобранные — наверх, по убыванию суммы
                        ])).sort((a, b) => (b.usdt || 0) - (a.usdt || 0)).map((r, i) => (
                          <TableRow key={i}>
                            <TableCell>{r.label}</TableCell>
                            <TableCell>{r.net}</TableCell>
                            <TableCell sx={{ fontSize: '0.65rem', fontFamily: 'monospace', display: { xs: 'none', md: 'table-cell' } }}>
                              {r.address}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: r.usdt > 0 ? 700 : 400 }}>
                              {fmtBal(r.usdt, 2)}
                            </TableCell>
                            <TableCell align="right">
                              {fmtBal(r.gas, r.gasCoin === 'BNB' ? 5 : 3)} {r.gasCoin}
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                              <Button size="small" disabled={!!treasuryBusy || !(r.usdt > 0)}
                                onClick={() => sweepWallet(r.telegramId, r.network)}>
                                {treasuryBusy === `sweep-${r.telegramId}-${r.network}` ? 'Собираю…' : 'Собрать'}
                              </Button>
                              <Button size="small" color="warning" disabled={!!treasuryBusy || !(r.usdt > 0)}
                                onClick={() => sendFromUser(r)}>
                                {treasuryBusy === `usend-${r.telegramId}-${r.network}` ? 'Шлю…' : 'Отправить'}
                              </Button>
                              <Button size="small" disabled={!!treasuryBusy}
                                onClick={() => topupGas(r.telegramId, r.network)}>
                                {treasuryBusy === `gas-${r.telegramId}-${r.network}` ? '⛽…' : '⛽ Газ'}
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {treasury.users.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 1, textAlign: 'center' }}>
                      Кошельки юзерам ещё не выдавались
                    </Typography>
                  )}
                </>
              )}
            </Paper>

            {/* Назначение индивидуальной цены */}
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'rgba(255,255,255,0.03)' }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>💲 Индивидуальная цена премиума (перебивает базовую)</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField size="small" label="@username или ID"
                  value={priceForm.identifier}
                  onChange={(e) => setPriceForm({ ...priceForm, identifier: e.target.value })}
                  sx={{ flex: '1 1 200px' }} />
                <TextField size="small" label="Цена $/мес" type="number"
                  value={priceForm.price}
                  onChange={(e) => setPriceForm({ ...priceForm, price: e.target.value })}
                  sx={{ width: 130 }} />
                <Button variant="contained" onClick={savePremiumPrice}>Назначить</Button>
              </Box>
              {priceMsg.text && (
                <Alert severity={priceMsg.type} sx={{ mt: 1, py: 0.25 }}
                  onClose={() => setPriceMsg({ type: '', text: '' })}>
                  {priceMsg.text}
                </Alert>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Юзер должен быть в базе (хоть раз заходил в игру/бота). Тарифы 3/6 мес считаются от этой цены.
                Повторное назначение перезаписывает цену. Список — во вкладке «Цены» ниже.
              </Typography>
            </Paper>

            {/* Переключатель разделов */}
            <Tabs value={premiumView}
              onChange={(e, v) => switchPremiumView(v)}
              variant="scrollable" scrollButtons="auto"
              sx={{ mb: 1, minHeight: 40, '& .MuiTab-root': { minHeight: 40, fontSize: '0.75rem' } }}>
              <Tab label="Кошельки" value="wallets" />
              <Tab label="Подписчики" value="subscribers" />
              <Tab label="Платежи" value="payments" />
              <Tab label="Подарки" value="gifts" />
              <Tab label="Цены" value="prices" />
            </Tabs>

            <TableContainer component={Paper} sx={{ bgcolor: 'rgba(255,255,255,0.02)' }}>
              <Table size="small">
                {premiumView === 'wallets' && (
                  <>
                    <TableHead><TableRow>
                      <TableCell>Юзер</TableCell><TableCell>BEP-20 / TON</TableCell>
                      <TableCell align="right">На адресе</TableCell>
                      <TableCell align="right">Собрано</TableCell>
                      <TableCell>Статус</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {premiumRows.map((r, i) => (
                        <TableRow key={i}>
                          <TableCell>{r.label}</TableCell>
                          <TableCell sx={{ fontSize: '0.65rem', fontFamily: 'monospace' }}>
                            {r.bscAddress}<br/>{r.tonAddress}
                          </TableCell>
                          <TableCell align="right">{r.bscBalance.toFixed(2)} / {r.tonBalance.toFixed(2)}</TableCell>
                          <TableCell align="right">{r.collected.toFixed(2)}</TableCell>
                          <TableCell>{r.subActive ? '✅' : (r.watching ? '👀' : '—')}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </>
                )}
                {premiumView === 'subscribers' && (
                  <>
                    <TableHead><TableRow>
                      <TableCell>Юзер</TableCell><TableCell>До</TableCell>
                      <TableCell align="right">Мес</TableCell>
                      <TableCell align="right">Всего $</TableCell><TableCell>Статус</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {premiumRows.map((r, i) => (
                        <TableRow key={i}>
                          <TableCell>{r.label}</TableCell>
                          <TableCell>{r.paidUntil ? new Date(r.paidUntil).toLocaleDateString('ru-RU') : '—'}</TableCell>
                          <TableCell align="right">{r.monthsPaid}</TableCell>
                          <TableCell align="right">{r.totalPaid.toFixed(2)}</TableCell>
                          <TableCell>{r.active ? '✅ активна' : '⌛ истекла'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </>
                )}
                {premiumView === 'payments' && (
                  <>
                    <TableHead><TableRow>
                      <TableCell>Дата</TableCell><TableCell>Юзер</TableCell>
                      <TableCell>Сеть</TableCell><TableCell align="right">Сумма</TableCell>
                      <TableCell align="right">$</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {premiumRows.map((r, i) => (
                        <TableRow key={i}>
                          <TableCell>{new Date(r.detectedAt).toLocaleString('ru-RU')}</TableCell>
                          <TableCell>{r.label}</TableCell>
                          <TableCell>{r.network === 'bsc' ? 'BEP-20' : 'TON'}</TableCell>
                          <TableCell align="right">{r.amount.toFixed(2)}</TableCell>
                          <TableCell align="right">{r.usd.toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </>
                )}
                {premiumView === 'gifts' && (
                  <>
                    <TableHead><TableRow>
                      <TableCell>Дата</TableCell><TableCell>От</TableCell>
                      <TableCell>Кому</TableCell><TableCell align="right">Мес</TableCell>
                      <TableCell align="right">$</TableCell><TableCell>Статус</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {premiumRows.map((r, i) => (
                        <TableRow key={i}>
                          <TableCell>{new Date(r.createdAt).toLocaleDateString('ru-RU')}</TableCell>
                          <TableCell>{r.gifter}</TableCell>
                          <TableCell>{r.recipient}</TableCell>
                          <TableCell align="right">{r.months}</TableCell>
                          <TableCell align="right">{r.price.toFixed(0)}</TableCell>
                          <TableCell>{r.status === 'paid' ? '✅' : (r.status === 'pending' ? '⏳' : '❌')}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </>
                )}
                {premiumView === 'prices' && (
                  <>
                    <TableHead><TableRow>
                      <TableCell>Юзер</TableCell><TableCell align="right">Цена $/мес</TableCell>
                      <TableCell>Назначено</TableCell><TableCell align="right">Действие</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {premiumRows.map((r, i) => (
                        <TableRow key={i}>
                          <TableCell>{r.label}</TableCell>
                          <TableCell align="right">${r.price.toFixed(0)}</TableCell>
                          <TableCell>{new Date(r.setAt).toLocaleDateString('ru-RU')}</TableCell>
                          <TableCell align="right">
                            <Button size="small" color="warning" onClick={() => resetPremiumPrice(r.telegramId)}>
                              Сброс
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </>
                )}
              </Table>
              {premiumRows.length === 0 && (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Пусто</Typography>
                </Box>
              )}
            </TableContainer>

            {/* Пагинация (кроме цен) */}
            {premiumView !== 'prices' && premiumTotal > premiumPageSize && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 2 }}>
                <Button size="small" disabled={premiumPage <= 1} onClick={() => changePremiumPage(-1)}>← Назад</Button>
                <Typography variant="body2">
                  Стр. {premiumPage} / {Math.max(1, Math.ceil(premiumTotal / premiumPageSize))}
                </Typography>
                <Button size="small"
                  disabled={premiumPage >= Math.ceil(premiumTotal / premiumPageSize)}
                  onClick={() => changePremiumPage(1)}>Вперёд →</Button>
              </Box>
            )}
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={6}>
          <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Typography variant="h6">Звонки Джарвису</Typography>
              <Button size="small" startIcon={<Refresh />} onClick={loadCalls} disabled={callLoading}>
                Обновить
              </Button>
            </Box>

            {callTotals && (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {[
                  { label: 'Звонков за 30 дней', value: callTotals.calls },
                  { label: 'Наговорили', value: `${Math.round(callTotals.seconds / 60)} мин` },
                  { label: 'Звонивших', value: callTotals.people },
                ].map((card) => (
                  <Grid item xs={12} sm={4} key={card.label}>
                    <Paper sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.03)' }}>
                      <Typography variant="body2" color="text.secondary">{card.label}</Typography>
                      <Typography variant="h5">{card.value}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}

            {callError && (
              <Typography color="error" sx={{ mb: 2 }}>
                Не смог получить звонки: {callError}
              </Typography>
            )}

            {callPeople.length === 0 && !callLoading && !callError && (
              <Typography color="text.secondary">Звонков пока не было.</Typography>
            )}

            {callPeople.map((person) => (
              <Paper key={person.telegram_id} sx={{ mb: 1.5, bgcolor: 'rgba(255,255,255,0.03)' }}>
                <Box
                  onClick={() => openCalls(person.telegram_id)}
                  sx={{ p: 2, display: 'flex', justifyContent: 'space-between',
                        alignItems: 'center', cursor: 'pointer' }}
                >
                  <Box>
                    <Typography>
                      {person.first_name || person.username || person.telegram_id}
                      {person.username ? ` (@${person.username})` : ''}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {person.calls} звонков · {Math.round(person.seconds / 60)} мин ·
                      последний {msk(person.last_call)}
                    </Typography>
                  </Box>
                  <Button size="small">
                    {callOpen === person.telegram_id ? 'Скрыть' : 'Расшифровки'}
                  </Button>
                </Box>

                {callOpen === person.telegram_id && (
                  <Box sx={{ px: 2, pb: 2 }}>
                    {callRows.length === 0 && (
                      <Typography variant="body2" color="text.secondary">Загружаю…</Typography>
                    )}

                    {/* Каждый звонок сложен и разворачивается отдельно */}
                    {callRows
                      .slice((callPage - 1) * CALLS_PER_PAGE, callPage * CALLS_PER_PAGE)
                      .map((call) => (
                      <Paper key={call.id} sx={{ mb: 1, bgcolor: 'rgba(0,0,0,0.25)' }}>
                        <Box
                          onClick={() => setOpenCall(openCall === call.id ? null : call.id)}
                          sx={{ p: 1.5, cursor: 'pointer', display: 'flex',
                                justifyContent: 'space-between', alignItems: 'center', gap: 1 }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2">
                              {msk(call.started_at)} ·
                              {' '}{call.direction || '—'} ·{' '}
                              {Math.floor(call.seconds / 60)}:{String(call.seconds % 60).padStart(2, '0')}
                              {call.audio_file ? ' · 🎧' : ''}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', overflow: 'hidden',
                                    textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            >
                              {call.summary || (call.scene ? `он ${call.scene}` : 'без итога')}
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="primary" sx={{ flexShrink: 0 }}>
                            {openCall === call.id ? 'свернуть' : 'открыть'}
                          </Typography>
                        </Box>

                        {openCall === call.id && (
                          <Box sx={{ px: 1.5, pb: 1.5 }}>
                            {call.scene && (
                              <Typography variant="caption" color="text.secondary">
                                Фоном было слышно: {call.scene}
                              </Typography>
                            )}
                            {call.audio_file && (
                              callAudio[call.id] ? (
                                <audio
                                  controls
                                  autoPlay
                                  style={{ width: '100%', marginTop: 8, height: 34 }}
                                  src={callAudio[call.id]}
                                />
                              ) : (
                                <Button size="small" startIcon={<Phone />} sx={{ mt: 1 }}
                                        onClick={() => playCall(call.id)}>
                                  Слушать запись
                                </Button>
                              )
                            )}
                            <Box sx={{ mt: 1.5, maxHeight: 420, overflowY: 'auto' }}>
                              {(call.transcript || []).map((turn, i) => (
                                <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
                                  {turn.at !== undefined && (
                                    <span style={{ color: '#7a8699', marginRight: 6,
                                                   fontVariantNumeric: 'tabular-nums' }}>
                                      {atMark(turn.at)}
                                    </span>
                                  )}
                                  <b style={{
                                    color: turn.who === 'Джарвис' ? '#7fd1ff'
                                      : turn.who === 'В чат' ? '#8de08d' : '#ffd479',
                                  }}>
                                    {turn.who === 'В чат' ? '📨 Отправлено в чат:' : `${turn.who}:`}
                                  </b>{' '}
                                  <span style={{
                                    whiteSpace: turn.who === 'В чат' ? 'pre-wrap' : 'normal',
                                  }}>{stripTags(turn.text)}</span>
                                </Typography>
                              ))}
                              {(call.transcript || []).length === 0 && (
                                <Typography variant="body2" color="text.secondary">
                                  Расшифровки нет — звонок был до включения записи.
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        )}
                      </Paper>
                    ))}

                    {callRows.length > CALLS_PER_PAGE && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                        <Button size="small" disabled={callPage <= 1}
                                onClick={() => { setCallPage(callPage - 1); setOpenCall(null); }}>
                          Назад
                        </Button>
                        <Typography variant="caption" color="text.secondary">
                          {callPage} из {Math.ceil(callRows.length / CALLS_PER_PAGE)}
                        </Typography>
                        <Button size="small"
                                disabled={callPage >= Math.ceil(callRows.length / CALLS_PER_PAGE)}
                                onClick={() => { setCallPage(callPage + 1); setOpenCall(null); }}>
                          Вперёд
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}
              </Paper>
            ))}
          </Box>
        </TabPanel>

        {/* 🎁 Менеджеры и тестовые доступы */}
        <TabPanel value={tabValue} index={7}>
          <Box sx={{ p: { xs: 1.5, md: 3 } }}>
            <Box sx={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1,
              flexWrap: 'wrap', gap: 1, mb: 2,
            }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SupervisorAccount /> Менеджеры Джарвиса
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button variant="outlined" startIcon={<Refresh />}
                  onClick={() => loadManagers()} disabled={managersLoading}>
                  Обновить
                </Button>
                <Button variant="contained" startIcon={<PersonAdd />}
                  onClick={() => setManagerDialog(true)}>
                  Назначить менеджера
                </Button>
              </Box>
            </Box>

            <Alert severity="info" sx={{ mb: 3 }}>
              Менеджер пишет Джарвису «хочу выдать тестовый доступ», называет
              @username человека и подтверждает цифрой 1. Человек получает
              полный Level 2 на {trialDays} дн. и уведомление от Джарвиса.
              Права применяются в течение 60 секунд.
            </Alert>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              <TextField
                label="Срок доступа по умолчанию, дней"
                type="number"
                size="small"
                value={trialDaysInput}
                onChange={(e) => setTrialDaysInput(e.target.value)}
                sx={{ width: { xs: '100%', sm: 260 } }}
                inputProps={{ min: 1, max: 90 }}
              />
              <Button variant="outlined" startIcon={<Save />} onClick={saveTrialDays}>
                Сохранить срок
              </Button>
            </Box>

            {managersLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer sx={{ mb: 5 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Telegram ID</TableCell>
                      <TableCell>Username</TableCell>
                      <TableCell>Выдал всего</TableCell>
                      <TableCell>Сейчас активно</TableCell>
                      <TableCell>Заметка</TableCell>
                      <TableCell>Назначен</TableCell>
                      <TableCell>Действия</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {managers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                          Менеджеров пока нет
                        </TableCell>
                      </TableRow>
                    ) : (
                      managers.map((m) => (
                        <TableRow key={m.id}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {m.telegram_id || '— (ждёт первого сообщения)'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {m.current_username || m.username
                              ? '@' + (m.current_username || m.username)
                              : '—'}
                          </TableCell>
                          <TableCell>{m.grants_total}</TableCell>
                          <TableCell>
                            {Number(m.grants_active) > 0
                              ? <Chip size="small" color="success" label={m.grants_active} />
                              : '0'}
                          </TableCell>
                          <TableCell>{m.note || '—'}</TableCell>
                          <TableCell>
                            {m.created_at ? new Date(m.created_at).toLocaleDateString('ru-RU') : '—'}
                          </TableCell>
                          <TableCell>
                            <Tooltip title="Показать только его выдачи">
                              <IconButton size="small" color="primary"
                                disabled={!m.telegram_id}
                                onClick={() => {
                                  const next = String(m.telegram_id) === trialFilter
                                    ? '' : String(m.telegram_id);
                                  setTrialFilter(next);
                                  loadManagers(next);
                                }}>
                                <FilterList />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Снять менеджера">
                              <IconButton size="small" color="error"
                                onClick={() => removeManager(m)}>
                                <PersonRemove />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Лог выдач */}
            <Box sx={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1,
              flexWrap: 'wrap', gap: 1, mb: 2,
            }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <CardGiftcard /> Выданные доступы
                {trialFilter && (
                  <Chip size="small" label="фильтр по менеджеру" onDelete={() => {
                    setTrialFilter('');
                    loadManagers('');
                  }} />
                )}
              </Typography>
              <Button variant="contained" startIcon={<CardGiftcard />}
                onClick={() => setTrialDialog(true)}>
                Выдать доступ
              </Button>
            </Box>

            {trialStats && (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} md={3}>
                  <StatCard title="Всего выдач" value={trialStats.total} icon={<CardGiftcard />} />
                </Grid>
                <Grid item xs={6} md={3}>
                  <StatCard title="Активны сейчас" value={trialStats.active} icon={<Star />} color="#FFC107" />
                </Grid>
                <Grid item xs={6} md={3}>
                  <StatCard title="За 7 дней" value={trialStats.last_week} icon={<TrendingUp />} color="#2196F3" />
                </Grid>
                <Grid item xs={6} md={3}>
                  <StatCard title="Людей охвачено" value={trialStats.people} icon={<People />} color="#9C27B0" />
                </Grid>
              </Grid>
            )}

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Кому</TableCell>
                    <TableCell>Telegram ID</TableCell>
                    <TableCell>Кто выдал</TableCell>
                    <TableCell>Срок</TableCell>
                    <TableCell>Выдан</TableCell>
                    <TableCell>Действует до</TableCell>
                    <TableCell>Статус</TableCell>
                    <TableCell>Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {trialGrants.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        Выдач пока не было
                      </TableCell>
                    </TableRow>
                  ) : (
                    trialGrants.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>
                          {row.current_username || row.username
                            ? '@' + (row.current_username || row.username)
                            : '—'}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {row.telegram_id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {row.granted_by_label}
                          {row.source === 'admin' && (
                            <Chip size="small" label="админ" sx={{ ml: 1 }} />
                          )}
                        </TableCell>
                        <TableCell>{row.days} дн.</TableCell>
                        <TableCell>{msk(row.started_at)}</TableCell>
                        <TableCell>{msk(row.expires_at)}</TableCell>
                        <TableCell>
                          {row.status === 'active' && (
                            <Chip size="small" color="success" label="активен" />
                          )}
                          {row.status === 'expired' && (
                            <Chip size="small" label={row.bought_premium ? 'истёк · купил премиум' : 'истёк'}
                              color={row.bought_premium ? 'primary' : 'default'} />
                          )}
                          {row.status === 'revoked' && (
                            <Chip size="small" color="error"
                              label={`отозван (${row.revoked_by || '—'})`} />
                          )}
                        </TableCell>
                        <TableCell>
                          {row.status === 'active' ? (
                            <Tooltip title="Отозвать доступ">
                              <IconButton size="small" color="error" onClick={() => revokeTrial(row)}>
                                <PersonRemove />
                              </IconButton>
                            </Tooltip>
                          ) : '—'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </TabPanel>

      </Paper>

      {/* Диалог назначения менеджера */}
      <Dialog open={managerDialog} onClose={() => setManagerDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Назначить менеджера</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="@username или Telegram ID"
                value={managerForm.identifier}
                onChange={(e) => setManagerForm({ ...managerForm, identifier: e.target.value })}
                placeholder="@manager или 592457038"
                helperText="Если человека нет в базе игры — сохраню по username, права включатся, когда он напишет Джарвису"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Заметка"
                value={managerForm.note}
                onChange={(e) => setManagerForm({ ...managerForm, note: e.target.value })}
                placeholder="Например: продажи, СНГ"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManagerDialog(false)}>Отмена</Button>
          <Button variant="contained" onClick={addManager}>Назначить</Button>
        </DialogActions>
      </Dialog>

      {/* Диалог выдачи тестового доступа руками */}
      <Dialog open={trialDialog} onClose={() => setTrialDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Выдать тестовый доступ</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="@username или Telegram ID"
                value={trialForm.identifier}
                onChange={(e) => setTrialForm({ ...trialForm, identifier: e.target.value })}
                placeholder="@user или 592457038"
                helperText="Джарвис найдёт человека и сам ему напишет. Если он ни разу не писал Джарвису — доступ выдам, но уведомление не дойдёт"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label={`Дней (пусто — по умолчанию ${trialDays})`}
                value={trialForm.days}
                onChange={(e) => setTrialForm({ ...trialForm, days: e.target.value })}
                inputProps={{ min: 1, max: 90 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Заметка"
                value={trialForm.note}
                onChange={(e) => setTrialForm({ ...trialForm, note: e.target.value })}
                placeholder="Например: по просьбе менеджера"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTrialDialog(false)}>Отмена</Button>
          <Button variant="contained" onClick={grantTrial} disabled={trialBusy}>
            {trialBusy ? 'Выдаю…' : 'Выдать'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог выдачи Level 2 */}
      <Dialog open={forcedLevel2Dialog} onClose={() => setForcedLevel2Dialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Выдать Level 2</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Telegram ID или Username"
                value={forcedLevel2Form.identifier}
                onChange={(e) => setForcedLevel2Form({ ...forcedLevel2Form, identifier: e.target.value })}
                placeholder="592457038 или @username или user_name"
                helperText="Можно ввести числовой ID, @username или username (с подчёркиваниями тоже работает)"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Заметка (необязательно)"
                value={forcedLevel2Form.note}
                onChange={(e) => setForcedLevel2Form({ ...forcedLevel2Form, note: e.target.value })}
                placeholder="Причина выдачи Level 2"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForcedLevel2Dialog(false)}>Отмена</Button>
          <Button
            variant="contained"
            onClick={grantForcedLevel2}
            disabled={!forcedLevel2Form.identifier.trim()}
            startIcon={<Star />}
          >
            Выдать Level 2
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог лимита картинок */}
      <Dialog open={videoAccessDialog} onClose={() => setVideoAccessDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Открыть доступ к видео</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Telegram ID или Username"
                value={videoAccessForm.identifier}
                onChange={(e) => setVideoAccessForm({ ...videoAccessForm, identifier: e.target.value })}
                placeholder="592457038 или @username"
                helperText="Числовой ID, @username или username. Это доступ БЕЗ оплаты — оплатившим он не нужен."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Заметка (необязательно)"
                value={videoAccessForm.note}
                onChange={(e) => setVideoAccessForm({ ...videoAccessForm, note: e.target.value })}
                placeholder="Кому и за что открыли"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVideoAccessDialog(false)}>Отмена</Button>
          <Button
            variant="contained"
            onClick={saveVideoAccess}
            disabled={!videoAccessForm.identifier.trim()}
          >
            Открыть
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={imageLimitDialog} onClose={() => setImageLimitDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Лимит генераций картинок</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Telegram ID или Username"
                value={imageLimitForm.identifier}
                onChange={(e) => setImageLimitForm({ ...imageLimitForm, identifier: e.target.value })}
                placeholder="592457038 или @username"
                helperText="Числовой ID, @username или username"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Лимит генераций в день"
                value={imageLimitForm.limit}
                onChange={(e) => setImageLimitForm({ ...imageLimitForm, limit: e.target.value })}
                placeholder="50"
                helperText="Дефолт без записи — 20/день. 0 = запретить генерации"
                inputProps={{ min: 0, max: 10000 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Заметка (необязательно)"
                value={imageLimitForm.note}
                onChange={(e) => setImageLimitForm({ ...imageLimitForm, note: e.target.value })}
                placeholder="Причина повышенного лимита"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageLimitDialog(false)}>Отмена</Button>
          <Button
            variant="contained"
            onClick={saveImageLimit}
            disabled={!imageLimitForm.identifier.trim() || imageLimitForm.limit === ''}
          >
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог просмотра сообщений */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Диалог с {selectedConversation?.first_name || selectedConversation?.username || 'пользователем'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            {conversationMessages.map((msg, idx) => (
              <Box
                key={idx}
                sx={{
                  mb: 2,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: msg.role === 'user'
                    ? 'rgba(33, 150, 243, 0.1)'
                    : 'rgba(76, 175, 80, 0.1)',
                  borderLeft: 4,
                  borderColor: msg.role === 'user' ? '#2196F3' : '#4CAF50'
                }}
              >
                <Typography variant="caption" color="textSecondary">
                  {msg.role === 'user' ? 'Пользователь' : 'Jarvis'} |{' '}
                  {new Date(msg.created_at).toLocaleString('ru-RU')}
                  {msg.is_voice && ' | Голос'}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                  {msg.message_text}
                </Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Закрыть</Button>
        </DialogActions>
      </Dialog>

      {/* Диалог добавления/редактирования знания */}
      <Dialog open={knowledgeDialogOpen} onClose={() => setKnowledgeDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingKnowledge ? 'Редактировать знание' : 'Добавить знание'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Категория</InputLabel>
                <Select
                  value={knowledgeForm.category}
                  label="Категория"
                  onChange={(e) => setKnowledgeForm({ ...knowledgeForm, category: e.target.value })}
                >
                  <MenuItem value="general">general — Общее</MenuItem>
                  <MenuItem value="staking">staking — Стейкинг</MenuItem>
                  <MenuItem value="referral">referral — Рефералы</MenuItem>
                  <MenuItem value="faq">faq — Частые вопросы</MenuItem>
                  <MenuItem value="token">token — Токен SPERM</MenuItem>
                  <MenuItem value="ranks">ranks — Ранги</MenuItem>
                  <MenuItem value="vip">vip — VIP-статусы</MenuItem>
                  <MenuItem value="leader">leader — Лидерские статусы</MenuItem>
                  <MenuItem value="minigames">minigames — Мини-игры</MenuItem>
                  <MenuItem value="mechanics">mechanics — Механики игры</MenuItem>
                  <MenuItem value="overview">overview — Обзор проекта</MenuItem>
                  <MenuItem value="social">social — Соцсети</MenuItem>
                  <MenuItem value="burn">burn — Сжигание</MenuItem>
                  <MenuItem value="education">education — Обучение</MenuItem>
                  <MenuItem value="earnings">earnings — Заработок</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={10}
                label="Текст знания"
                value={knowledgeForm.text}
                onChange={(e) => setKnowledgeForm({ ...knowledgeForm, text: e.target.value })}
                helperText="Информация, которую Jarvis будет использовать для ответов на вопросы пользователей"
                placeholder="Например: Lunar Staking — это основной тип стейкинга в TonMoon. Минимальный депозит 100 SPERM, базовая доходность 0.5% в день..."
              />
            </Grid>

            <Grid item xs={12}>
              <Alert severity="info">
                Jarvis использует эту информацию для ответов на вопросы пользователей через векторный поиск (RAG).
                Пишите чётко и информативно.
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKnowledgeDialogOpen(false)}>Отмена</Button>
          <Button
            variant="contained"
            onClick={saveKnowledge}
            disabled={!knowledgeForm.text.trim()}
          >
            {editingKnowledge ? 'Сохранить' : 'Добавить'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог создания/редактирования рассылки */}
      <Dialog open={broadcastDialogOpen} onClose={() => setBroadcastDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingBroadcast ? 'Редактировать рассылку' : 'Создать рассылку'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Название рассылки"
                value={broadcastForm.title}
                onChange={(e) => setBroadcastForm({ ...broadcastForm, title: e.target.value })}
                helperText="Внутреннее название для админки"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Суть сообщения"
                value={broadcastForm.message_hint}
                onChange={(e) => setBroadcastForm({ ...broadcastForm, message_hint: e.target.value })}
                helperText="Что Jarvis должен донести до пользователя. Он сам сформулирует текст."
                placeholder="Например: Сообщи пользователям о новом турнире с призовым фондом 10000 SPERM. Регистрация до 15 декабря."
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Призыв к действию (CTA)"
                value={broadcastForm.call_to_action}
                onChange={(e) => setBroadcastForm({ ...broadcastForm, call_to_action: e.target.value })}
                helperText="Что пользователь должен сделать"
                placeholder="Например: Зарегистрироваться в турнире"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Стиль доставки</InputLabel>
                <Select
                  value={broadcastForm.delivery_style}
                  label="Стиль доставки"
                  onChange={(e) => setBroadcastForm({ ...broadcastForm, delivery_style: e.target.value })}
                >
                  <MenuItem value="soft">Мягкий — в контексте разговора</MenuItem>
                  <MenuItem value="direct">Прямой — сразу к делу</MenuItem>
                  <MenuItem value="urgent">Срочный — с акцентом на важность</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Приоритет</InputLabel>
                <Select
                  value={broadcastForm.priority}
                  label="Приоритет"
                  onChange={(e) => setBroadcastForm({ ...broadcastForm, priority: e.target.value })}
                >
                  <MenuItem value={1}>Низкий — доставить когда удобно</MenuItem>
                  <MenuItem value={2}>Средний — в течение дня</MenuItem>
                  <MenuItem value={3}>Высокий — как можно скорее</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FilterList /> Таргетинг (опционально)
              </Typography>
              <Typography variant="caption" color="textSecondary" paragraph>
                Оставьте пустым для отправки всем пользователям
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={broadcastForm.target_filter?.hasStaking || false}
                        onChange={(e) => setBroadcastForm({
                          ...broadcastForm,
                          target_filter: { ...broadcastForm.target_filter, hasStaking: e.target.checked }
                        })}
                      />
                    }
                    label="Со стейкингом"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={broadcastForm.target_filter?.hasReferrals || false}
                        onChange={(e) => setBroadcastForm({
                          ...broadcastForm,
                          target_filter: { ...broadcastForm.target_filter, hasReferrals: e.target.checked }
                        })}
                      />
                    }
                    label="С рефералами"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={broadcastForm.target_filter?.isVip || false}
                        onChange={(e) => setBroadcastForm({
                          ...broadcastForm,
                          target_filter: { ...broadcastForm.target_filter, isVip: e.target.checked }
                        })}
                      />
                    }
                    label="Только VIP"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={broadcastForm.target_filter?.isLeader || false}
                        onChange={(e) => setBroadcastForm({
                          ...broadcastForm,
                          target_filter: { ...broadcastForm.target_filter, isLeader: e.target.checked }
                        })}
                      />
                    }
                    label="Только лидеры"
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBroadcastDialogOpen(false)}>Отмена</Button>
          <Button
            variant="contained"
            onClick={saveBroadcast}
            disabled={!broadcastForm.title || !broadcastForm.message_hint}
          >
            {editingBroadcast ? 'Сохранить' : 'Создать'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог добавления разрешённой ссылки */}
      <Dialog open={domainDialogOpen} onClose={() => setDomainDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Добавить разрешённую ссылку</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
            Добавляйте <strong>конкретные ссылки</strong>, а не домены! Например:
            <br/>• <code>t.me/safepermoon</code> — конкретный канал
            <br/>• <code>t.me/moonraid_game_bot</code> — конкретный бот
            <br/>• <code>safepermoon.com</code> — весь сайт
          </Alert>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Разрешённая ссылка"
                value={newDomain.domain}
                onChange={(e) => setNewDomain({ ...newDomain, domain: e.target.value })}
                placeholder="t.me/channel_name"
                helperText="Формат: t.me/username или domain.com"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Описание"
                value={newDomain.description}
                onChange={(e) => setNewDomain({ ...newDomain, description: e.target.value })}
                placeholder="Например: Официальный канал TonMoon"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDomainDialogOpen(false)}>Отмена</Button>
          <Button
            variant="contained"
            onClick={addAllowedDomain}
            disabled={!newDomain.domain.trim()}
          >
            Добавить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Jarvis;
